/**
 * Tool UIs for the Upright chat surface.
 *
 * Each `makeAssistantToolUI` definition renders the inline output for a
 * server-side tool call. The `CHAT_TOOL_UIS` array is the single export
 * that the main `Thread` component reads — adding a new tool UI here is
 * enough to wire it into the chat.
 *
 * PLT-7912: what a tool is doing, and that it ran, is the tool trail's job
 * (`tool-trail.tsx`): one line per turn, listing every tool call. So a tool UI
 * here draws only what belongs in the answer itself — a card, a limit notice,
 * a generated image — and nothing while its tool runs.
 */
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { makeAssistantToolUI, useAssistantRuntime, useMessage, useThread } from '@assistant-ui/react';
import { CheckStreamlineCoreLine, InfoCircle } from '@upright/ui/icons';
import { UprightLogoSymbolBlackWhite } from '@upright/ui/common/generic/components/icons/logo/upright-logo-symbol-black-white';
import { VisuallyHidden } from '@upright/ui/common/generic/components/visually-hidden';
import type { ClientCitation } from '@upright/common/chat/citation-types';
import { useGetCompanyVariantId } from '@upright/ui/views/model-entity-views/company-view/hooks/use-get-company-variant-id';
import { useUnifiedShell } from '@upright/ui/views/unified-shell/use-unified-shell';
import { Link } from 'react-router-dom';
import { getCitation, getCitationByIndex, type RegisteredCitation } from './citation-registry';
import { Callout, KpiTileRow, coerceTone, type KpiTileProps, type Tone } from './components';
import { OpDataBatchCard, type OpDataBatchCardProps } from './op-data-batch-card';
import {
  SustainabilityIndicatorsBatchCard,
  type SustainabilityIndicatorsBatchCardProps,
} from './sustainability-indicators-batch-card';
import { SiteLocationsBatchCard, type SiteLocationsBatchCardProps } from './site-locations-batch-card';
import { SupplierImportReviewCard } from './supplier-import-review-card';
import { ReviewRequestCard } from './review-request-card';
import type { ReviewRequestFields } from '@upright/common/chat/review-request-types';
import {
  approvedSitesOf,
  approvingPublishResult,
  PRODUCT_SET_CARD_APP_NAME,
  siteReviewOpenedBefore,
  siteReviewStanding,
} from './product-set-card-standing';
import { ProductSetChatCard } from './product-set-chat-card';
import { FinancialStatementOfferCard } from './financial-statement-offer-card';
import type { FinancialStatementOffer } from '@upright/common/mcp-payloads/financial-statement-offer';
import type { FinancialStatementImportPayload } from '@upright/common/mcp-payloads/financial-statement-import';
import type { GetSingleCompanyFinancialStatementResponse } from '@upright/common/model/input/financial-statements/api-types';
import { fetchOrThrow } from '@upright/ui/api/common/fetchers/fetch-or-throw';
import { refreshFinancialStatementDraft } from './financial-statement-draft-refresh';
import { isSeededToolCallId } from './history/seed-message';
import { TailoringBatchCard, type TailoringBatchCardProps } from './tailoring-batch-card';
import { TailoringRemovalCard, type TailoringRemovalCardProps } from './tailoring-removal-card';
import { TailoringSelectionCard, type TailoringSelectionCardProps } from './tailoring-selection-card';
import { OwnInsightsStartCard, type OwnInsightsStartCardProps } from './own-insights-start-card';
import { ChatTokenLimitCard, ChatToolLimitCard, type ChatToolLimitKind } from './token-limit-card';
// `ChatMarkdown` is loaded lazily (see EmitCalloutToolUI below) to break the
// module-init cycle with `upright-chat-markdown.tsx`, which imports the inline
// renderers exported from this file. ES module cycles are fine for function-
// only access — the imported `ChatMarkdown` binding is only read at render
// time, by which point both modules have finished loading.
import { ChatMarkdown } from './upright-chat-markdown';
import { ChatMcpAppIframe } from './upright-chat-ui-common';
import { useIsChatInternalMode } from '../common/generic/hooks';
import { composerState, type ToolResultWithUi } from './upright-chat-shared';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FINANCIAL_STATEMENTS_QUERY_KEY,
  HAS_UNINTEGRATED_FINANCIAL_STATEMENT_CHANGES_QUERY_KEY,
} from '@upright/ui/views/dma-v2-view/dashboard/financial-statements-input/query-keys';
import { sameOriginPlatformPath, tryParsePlatformCompanyId, tryParsePlatformLinkHost } from './upright-platform-link';
import { getCompanyVisibility, registerCompanyVisibility } from './visibility-registry';
import { isFollowupUsed, markFollowupUsed, subscribeFollowupsUsed } from './followup-used-registry';
import { uploadsToResend } from './followup-resend-uploads';
import {
  impactCategorySuffix,
  impactReasoningSuffix,
  matterSuffix,
  trajectoryLocationsSuffix,
} from './tool-trail-steps';
import { withToolTrail } from './tool-trail';

// ---------------------------------------------------------------------------
// Error card — also used as a synthetic tool-call surface (see makeErrorContent
// in upright-chat-shared.ts).
// ---------------------------------------------------------------------------

/**
 * Inline error card shown when a chat request fails.
 * Replaces raw "**Error:** ..." markdown with a styled, user-friendly block.
 */
function ChatErrorMessage({ message }: { message: string }) {
  return (
    <div className="chat-error">
      <InfoCircle className="chat-error-icon" width={16} height={16} aria-hidden="true" />
      <div className="chat-error-body">
        <span className="chat-error-title">Something went wrong</span>
        <span className="chat-error-detail">{message}</span>
      </div>
    </div>
  );
}

/** Tool UI for the synthetic "chat_error" tool — renders the error card. */
const ChatErrorToolUI = makeAssistantToolUI<{ message: string }, Record<string, never>>({
  toolName: 'chat_error',
  render: ({ args }) => <ChatErrorMessage message={args.message} />,
});

/**
 * PLT-5349: informational notice card. Non-error counterpart to
 * `ChatErrorMessage` — used for conversation-length notices (the model is
 * still answering, nothing failed), so it's styled in the neutral lilac
 * informational palette rather than the red error palette.
 */
function ChatNoticeMessage({ message }: { message: string }) {
  return (
    <div className="chat-notice">
      <InfoCircle className="chat-notice-icon" width={16} height={16} aria-hidden="true" />
      <span className="chat-notice-detail">{message}</span>
    </div>
  );
}

/**
 * Tool UI for the synthetic "chat_notice" tool — renders the informational
 * notice card. Synthesised client-side in the adapter from `context-warning`
 * / `context-trimmed` SSE events (see `upright-chat-adapter.ts`).
 */
const ChatNoticeToolUI = makeAssistantToolUI<{ message: string }, Record<string, never>>({
  toolName: 'chat_notice',
  render: ({ args }) => <ChatNoticeMessage message={args.message} />,
});

/**
 * PLT-5003: tool UI for the synthetic "ai_chat_token_limit" tool — renders the
 * friendly "you've reached your usage limit" card (with a tier-specific CTA)
 * when the backend returns 429 `AI_CHAT_TOKEN_LIMIT_REACHED`. Synthesised
 * client-side by `makeTokenLimitContent` in the adapter.
 *
 * PLT-5266: the optional `companyId` arg is the client company the blocked turn
 * was metered against; the card uses it to offer consultant admins a self-serve
 * Starter→Pro upgrade for that company.
 */
const ChatTokenLimitToolUI = makeAssistantToolUI<{ companyId?: string }, Record<string, never>>({
  toolName: 'ai_chat_token_limit',
  render: ({ args }) => <ChatTokenLimitCard companyId={args.companyId} />,
});

// ---------------------------------------------------------------------------
// Generic MCP-app tool UI shell — renders the MCP App iframe when the tool
// result includes UI metadata, or falls back to collapsible JSON.
// ---------------------------------------------------------------------------

/**
 * Bridge between MCP tool results and the chat-side `visibility-registry`
 * (PLT-4548). Company-scoped tool responses carry `companyId` plus
 * `visibility: 'private' | 'public'`; we copy that pair into the registry so
 * the right-margin sidenote next to a platform `[Company](/company/<id>/...)`
 * deep-link can render a lock badge for private results without the
 * markdown component re-fetching the tool payload.
 *
 * Best-effort: any unexpected payload shape is silently skipped — the
 * sidenote then renders without a badge, which is the same outcome as a
 * public result, never a confusing "wrong badge".
 *
 * Called from inside the rendering branch of every company-scoped tool
 * (NetImpactToolUI, DmaResultToolUI, …). Reading from a successful result
 * once per render is cheap and idempotent (same id maps to same value).
 */
function registerVisibilityFromToolResult(result: ToolResultWithUi | undefined): void {
  if (!result || typeof result !== 'object') return;
  const data = (result as { data?: unknown }).data;
  if (!data || typeof data !== 'object') return;
  const d = data as Record<string, unknown>;
  const companyId = typeof d.companyId === 'string' ? d.companyId : undefined;
  const visibility = d.visibility;
  if (!companyId) return;
  if (visibility !== 'private' && visibility !== 'public') return;
  const ownerOrganizationName =
    typeof d.ownerOrganizationName === 'string' && d.ownerOrganizationName.length > 0
      ? d.ownerOrganizationName
      : undefined;
  registerCompanyVisibility(companyId, { visibility, ownerOrganizationName });
}

/**
 * Tool UI component for get_net_impact_summary — renders the MCP App iframe
 * when the tool result includes UI metadata. Net impact has no per-call
 * disambiguator (companyId is shared across cards and isn't useful on
 * the title), so it routes through `McpToolUI` with just the base label.
 */
const NetImpactToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'get_net_impact_summary',
  render: ({ result, toolCallId }) => <McpToolUI baseLabel="Net Impact" result={result} toolCallId={toolCallId} />,
});

/** Renders an MCP App iframe or falls back to collapsible JSON */
function McpToolUI({
  baseLabel,
  contextSuffix,
  result,
  toolCallId,
  defaultExpanded,
  limitFeature,
  quiet = false,
}: {
  /**
   * Title-cased tool-class name (e.g. "Contributing Products", "DMA
   * Results"). Used both for the loading status line and as the
   * collapsed MCP App card title. Caller-supplied rather than
   * derived from `toolName` because the tool name (`get_dma_results`)
   * lower-cases the acronym after the prefix strip (PLT-4625 follow-
   * up issue 5 — the user explicitly asked for nicer casing in the
   * card title).
   */
  baseLabel: string;
  /**
   * Optional context-specific suffix that disambiguates a collapsed
   * card when multiple instances of the same tool appear in one
   * thread — e.g. ": Biodiversity" for a Contributing Products call
   * scoped to E4, or ": Climate change mitigation" for a Single
   * Matter call on E1-ccm. Concatenated with a `: ` separator iff
   * non-empty, so callers without a meaningful disambiguator pass
   * `undefined`/`null` and get just the bare base label.
   */
  contextSuffix?: string | null;
  result: ToolResultWithUi | undefined;
  /**
   * Identity of the tool call this card renders; keys the card (PLT-6891).
   * assistant-ui keys messages and content parts by index, and opening a saved
   * chat imports it into the already-mounted thread, so a card at the same
   * position in the next chat would otherwise reuse this card's iframe. The
   * iframe host delivers the tool result once, on the ext-apps handshake, so a
   * reused iframe keeps drawing the previous chat's data under the new header.
   */
  toolCallId: string;
  /**
   * Hard override that keeps the MCP App card expanded by default even
   * inside a multi-card stack (PLT-4997). Defaults to `false`, where a card
   * follows the sole-card auto-expand signal (a lone card opens, stacked
   * cards collapse). Set to `true` for cards that should stay open in a
   * stack: the comparison cards, the DD lifecycle cards (live job
   * progress), and the value-chain card (its diagram IS the answer —
   * PLT-4829). See `ChatMcpAppIframe` for the resolution.
   */
  defaultExpanded?: boolean;
  /**
   * PLT-5273: human-readable name of this tool's capped feature (e.g. "climate
   * risk map assessments"), passed to the `usage-limit-reached` card so it names
   * the exhausted feature. Only the quota-capped tools that route through this
   * shell set it; everyone else leaves it undefined (those tools never hit a
   * per-tool limit, so the card never renders for them).
   */
  limitFeature?: string;
  /** Hide background reads from the transcript, including their loading and debug rows. */
  quiet?: boolean;
}) {
  // PLT-5083: the raw-JSON fallback below is reachable only when the backend
  // omits `_ui.appName` — which per PLT-4743 it deliberately does when a data
  // tool returns no-data (`dataAvailable: false`) or errors.
  // That payload (companyId/variantId UUIDs, internal `reason` codes,
  // `availableOnReleases`, …) is debugging detail, not a customer-facing view,
  // so we gate it: team users in internal mode keep the JSON for debugging;
  // everyone else gets nothing and relies on the model's own reply text to
  // explain the no-data situation (which PLT-4743 already does). Must be called
  // unconditionally, before any early return, to satisfy the Rules of Hooks.
  const showInternalDebugJson = useIsChatInternalMode();

  // Quiet reads still inform visibility badges on links in the model's reply.
  registerVisibilityFromToolResult(result);
  if (quiet) return null;

  // PLT-7912: the tool trail shows the read in progress; the card comes with the result.
  if (!result) return null;

  // PLT-4664: include the company name on every company-scoped card so a
  // thread that mixes several companies (or several lenses for one company)
  // reads as "Net Impact: Acme Inc" / "DMA Results: Acme Inc" rather than
  // a stack of identical "Net impact" / "DMA Results" headers. The name is
  // only available once the result is in (tool args carry `companyId`, not
  // the resolved name). Tools whose
  // result payload doesn't include `companyName` (e.g. screen_companies-
  // style multi-company shells routed elsewhere) degrade to the existing
  // suffix-only behaviour.
  const companyName = extractCompanyNameFromResult(result);
  const suffixParts = [companyName, contextSuffix].filter((p): p is string => !!p);
  const label = suffixParts.length > 0 ? `${baseLabel}: ${suffixParts.join(' — ')}` : baseLabel;

  // PLT-5273: a per-tool usage-quota hit (e.g. climate-risk grid, physical
  // climate risk) arrives as an error result with no `_ui.appName`. Show the
  // shared limit card with its upgrade/contact CTA instead of falling through
  // to the silent no-data fallback below.
  const limitKind = extractToolLimitError(result);
  if (limitKind) {
    return <ChatToolLimitCard kind={limitKind} feature={limitFeature} />;
  }

  const ui = result._ui;
  // PLT-7433: web chat draws the product set card itself, with its editor inline; Claude and ChatGPT
  // render the MCP app for the same result.
  if (ui?.appName === PRODUCT_SET_CARD_APP_NAME) {
    return <ProductSetChatCard key={toolCallId} toolCallId={toolCallId} toolResult={result.data} />;
  }
  if (ui?.appName) {
    return (
      <ChatMcpAppIframe
        key={toolCallId}
        appName={ui.appName}
        toolResult={result.data}
        label={label}
        defaultExpanded={defaultExpanded}
      />
    );
  }

  // External users: render nothing for the no-data/error fallback so internal
  // artifacts never leak into the customer-facing chat (PLT-5083). The model's
  // accompanying reply text carries the no-data explanation.
  if (!showInternalDebugJson) {
    return null;
  }

  return (
    <details className="tool-result-details">
      <summary>{label}</summary>
      <pre className="tool-result-json">{JSON.stringify(result.data, null, 2)}</pre>
    </details>
  );
}

/**
 * Pull `companyName` off a company-scoped tool result for use in the card
 * title (PLT-4664). Returns `null` if the result isn't available yet, the
 * data shape doesn't include a string `companyName`, or the value is the
 * empty string — every caller treats `null` as "render the bare base label".
 */
function extractCompanyNameFromResult(result: ToolResultWithUi | undefined): string | null {
  if (!result || typeof result !== 'object') return null;
  const data = (result as { data?: unknown }).data;
  if (!data || typeof data !== 'object') return null;
  const name = (data as { companyName?: unknown }).companyName;
  return typeof name === 'string' && name.length > 0 ? name : null;
}

/**
 * PLT-5273: detect a per-tool usage-quota hit. The capped tools
 * (compute_financial_effects + the climate-risk / site tools) report a limit
 * as a tool result `{ error, _userFacing }` with no `_ui.appName` (chat/index.ts
 * skips the app card on tool failure). Returns the limit kind so the tool UI can
 * render the shared `ChatToolLimitCard` instead of its app card / status line,
 * or null for any other result (the generic tool-failure payload uses a
 * free-form `error` string, so it never matches here).
 */
function extractToolLimitError(result: ToolResultWithUi | undefined): ChatToolLimitKind | null {
  if (!result || typeof result !== 'object') return null;
  const data = (result as { data?: unknown }).data;
  if (!data || typeof data !== 'object') return null;
  const error = (data as { error?: unknown }).error;
  return error === 'subscription-required' || error === 'usage-limit-reached' ? error : null;
}

// PLT-7912: a company search is a row in the tool trail ("Found company Acme"), so it draws nothing
// in the message. Its match count, identifier matches included (PLT-4775), is read there.
const SearchToolUI = makeAssistantToolUI<{ query: string }, unknown>({
  toolName: 'search',
  render: () => null,
});

const ContributingProductsToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'get_contributing_products',
  render: props => (
    <McpToolUI
      baseLabel="Contributing Products"
      quiet={props.args.quiet === true}
      contextSuffix={impactCategorySuffix(props.args)}
      result={props.result}
      toolCallId={props.toolCallId}
    />
  ),
});

const DmaResultToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'get_dma_results',
  render: props => (
    <McpToolUI
      baseLabel="DMA Results"
      quiet={props.args.quiet === true}
      result={props.result}
      toolCallId={props.toolCallId}
    />
  ),
});

/** PLT-7363: the double materiality matrix card — topic 2×2 and matter 3×3. */
const DmaMatrixToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'get_dma_materiality_matrix',
  render: props => <McpToolUI baseLabel="Materiality Matrix" result={props.result} toolCallId={props.toolCallId} />,
});

const SupplierRiskToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'get_supplier_risk_assessment',
  render: props => <McpToolUI baseLabel="Supplier Risk" result={props.result} toolCallId={props.toolCallId} />,
});

/** PLT-7736: the supplier base card — the Supplier base page's figures and bars, and the suppliers behind them. */
const SupplierBaseToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'get_supplier_base_report',
  render: props => (
    <McpToolUI
      baseLabel="Supplier Base"
      contextSuffix={buyerCompanyNameSuffix(props.result)}
      result={props.result}
      toolCallId={props.toolCallId}
    />
  ),
});

/**
 * The buyer whose supplier base a card is, for its title: the payload names it `buyerCompanyName`, not
 * the `companyName` the shell reads, and a consultancy's thread holds a card per client.
 */
function buyerCompanyNameSuffix(result: ToolResultWithUi | undefined): string | null {
  const data = result?.data;
  if (!data || typeof data !== 'object') return null;
  const name = (data as { buyerCompanyName?: unknown }).buyerCompanyName;
  return typeof name === 'string' && name.length > 0 ? name : null;
}

/** PLT-7359: the executive financial risk and value creation report card and its topic drill-down. */
const InvestorReportToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'get_investor_report',
  render: props => (
    <McpToolUI
      baseLabel="Executive financial risk and value creation report"
      result={props.result}
      toolCallId={props.toolCallId}
    />
  ),
});

const SingleMatterToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'get_single_matter_summary',
  render: props => (
    <McpToolUI
      baseLabel="Single Matter"
      quiet={props.args.quiet === true}
      contextSuffix={matterSuffix(props.args)}
      result={props.result}
      toolCallId={props.toolCallId}
    />
  ),
});

const SupportingArticlesToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'get_supporting_articles',
  // Supporting articles are scoped to (product, impactTopic); the
  // impactTopicId is an opaque CSRD code without a friendly mapping in
  // common (`CREAGHGEMI`, `GENERWASTE`), so we don't disambiguate on
  // topic here — the productCode arg is a more user-recognisable
  // disambiguator and is short enough to show inline.
  render: props => {
    const productCode = typeof props.args.productCode === 'string' ? props.args.productCode : null;
    return (
      <McpToolUI
        baseLabel="Supporting Articles"
        contextSuffix={productCode}
        result={props.result}
        toolCallId={props.toolCallId}
      />
    );
  },
});

const FinancialEffectsToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'get_financial_effects_summary',
  render: props => <McpToolUI baseLabel="Financial Effects" result={props.result} toolCallId={props.toolCallId} />,
});

const ProductImpactReasoningToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'get_product_impact_reasoning',
  // The tool accepts a Net Impact code, DMA matterId, SDG id, or
  // free-form description — try the impact code path first (covers
  // E1/H1/etc. and the lens-routed `impact` arg) and fall back to
  // matter dispatch if the LLM passed a DMA matterId via the same
  // `impact` slot (`E1-ccm`-style).
  //
  // `defaultExpanded` (PLT-7519): the pathway diagram is what a "why does this
  // product cause this impact" answer is about, and the premise check the
  // model runs alongside it adds a second card, which would otherwise collapse
  // the pathway too.
  render: props => {
    const impact = impactReasoningSuffix(props.args);
    return (
      <McpToolUI
        baseLabel="Product Impact Reasoning"
        quiet={props.args.quiet === true}
        contextSuffix={impact}
        result={props.result}
        toolCallId={props.toolCallId}
        defaultExpanded={true}
      />
    );
  },
});

/**
 * PLT-4787 — taxonomy-level value-chain card. The product code itself is the
 * only useful disambiguator (taxonomy codes are short and user-recognisable),
 * and direction tells the user whether they're looking at upstream / downstream
 * / both for that focal. Two-part suffix mirrors the impact-pathway pattern
 * but keeps "8-char code · direction" compact in the collapsed card header.
 *
 * `defaultExpanded` is `true` here (PLT-4829), as on the impact pathway card:
 * the SVG tier diagram IS the answer here, and the other lens cards have prose
 * primitives (KPI tiles, tables, callouts) that carry the headline even when
 * the card is collapsed — the value-chain card does not, so collapsed-by-
 * default reads as "the diagram never drew" in narrow chat surfaces.
 */
const ProductValueChainToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'get_product_value_chain',
  render: props => {
    const productCode = typeof props.args.productCode === 'string' ? props.args.productCode : null;
    const direction = typeof props.args.direction === 'string' ? props.args.direction : null;
    const parts = [productCode, direction].filter((p): p is string => !!p);
    const suffix = parts.length > 0 ? parts.join(' · ') : null;
    return (
      <McpToolUI
        baseLabel="Product Value Chain"
        contextSuffix={suffix}
        result={props.result}
        toolCallId={props.toolCallId}
        defaultExpanded={true}
      />
    );
  },
});

// PLT-4474 — three new lens-aligned tools (SDG, EU Taxonomy, EU SFDR PAIs)
// each renders via the McpToolUI app shell so the iframe app card shows up
// when the backend response includes `_ui.appName`. The label string is what
// the user sees during the loading spinner phase, so make it human-friendly.
const SdgAlignmentToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'get_sdg_alignment',
  render: props => <McpToolUI baseLabel="SDG Alignment" result={props.result} toolCallId={props.toolCallId} />,
});

const EuTaxonomyToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'get_eu_taxonomy_summary',
  render: props => <McpToolUI baseLabel="EU Taxonomy" result={props.result} toolCallId={props.toolCallId} />,
});

const SfdrPaisToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'get_sfdr_pais_summary',
  render: props => <McpToolUI baseLabel="SFDR PAIs" result={props.result} toolCallId={props.toolCallId} />,
});

// ---------------------------------------------------------------------------
// Data-query tools whose value to the user is "we consulted this source", not
// the payload. Their status lines are rows in the tool trail (PLT-7912): the
// filters and match count of a screen, the query of a knowledge-base search.
// ---------------------------------------------------------------------------

const ScreenCompaniesToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'screen_companies',
  render: () => null,
});

const SearchKnowledgeBaseToolUI = makeAssistantToolUI<{ query: string }, ToolResultWithUi>({
  toolName: 'search_knowledge_base',
  render: () => null,
});

// PLT-4773: both multi-company tools now have dedicated MCP App cards
// (`multi-company-net-impact` and `multi-company-dma`). Route them through
// `McpToolUI` so the iframe app renders when the backend response includes
// `_ui.appName`. The previous count-style status-line helper
// (`MultiCompanyStatus`) was retired in the same change — `McpToolUI`
// already covers the pre-result spinner + post-result collapsed-card
// shells uniformly with the per-company tools.
const MultiCompanyNetImpactToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'get_multi_company_net_impact_summary',
  // Comparison card is the across-companies summary the user is reading —
  // keep it expanded even in a stack (PLT-4997).
  render: props => (
    <McpToolUI
      baseLabel="Net Impact Comparison"
      result={props.result}
      toolCallId={props.toolCallId}
      defaultExpanded={true}
    />
  ),
});

const MultiCompanyDmaToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'get_multi_company_dma_summary',
  // Comparison card stays expanded in a stack (PLT-4997) — see above.
  render: props => (
    <McpToolUI baseLabel="DMA Comparison" result={props.result} toolCallId={props.toolCallId} defaultExpanded={true} />
  ),
});

// PLT-5727 / PLT-5737: group exposure card for a group / portfolio — a paginated
// top-product breakdown, each product listing its top contributing holdings. The
// exposure breakdown is the answer the user is reading, so keep it expanded.
const GroupExposureToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'get_group_exposure',
  render: props => (
    <McpToolUI
      baseLabel="Portfolio Exposure"
      result={props.result}
      toolCallId={props.toolCallId}
      defaultExpanded={true}
    />
  ),
});

// ---------------------------------------------------------------------------
// PLT-4620 / PLT-4621 — Chat-driven DD company creation tool UIs.
//
// The backend tagged each result with `_ui: { appName: 'company-card' }` or
// `'product-set-card'` already (see registry.ts mcpAppResourceUri), but
// without these `makeAssistantToolUI` registrations the chat client falls
// back to the default text-only render and the card never mounts. Each of
// the five DD tools rides through `McpToolUI`, which honors the `_ui.appName`
// hint: the `CompanyCard` iframe, or the product set card web chat draws
// itself (`ProductSetChatCard`, PLT-7433).
//
// All five take `companyId` as the primary disambiguator on the args side,
// but the tool-result envelope already carries `companyName` — `McpToolUI`'s
// `extractCompanyNameFromResult` therefore appends the company name to the
// label automatically once the result lands, so a thread that creates two
// companies reads as "Company: Acme" / "Company: Beta" rather than two
// identical headers. No per-tool suffix needed here.
// ---------------------------------------------------------------------------
const CreateDdCompanyToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'create_dd_company',
  render: props => (
    <McpToolUI baseLabel="Company" result={props.result} toolCallId={props.toolCallId} defaultExpanded={true} />
  ),
});

// PLT-6267 — the supplier create rides the same CompanyCard. Its successful result is the same
// `DdCompanyResult` shape, and a supplier being assessed looks like any other company being
// assessed; a second card type would differ only in the word on the header.
//
// The other outcomes need no registration of their own: every one of them carries
// `dataAvailable: false`, which the chat host reads as "render no card", because nothing was
// created to put on one.
const CreateSupplierToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'create_supplier',
  render: props => (
    <McpToolUI baseLabel="Supplier" result={props.result} toolCallId={props.toolCallId} defaultExpanded={true} />
  ),
});

const GetDdCompanyStatusToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'get_dd_company_status',
  render: props => (
    <McpToolUI baseLabel="Company Status" result={props.result} toolCallId={props.toolCallId} defaultExpanded={true} />
  ),
});

const GetDdProductSetToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'get_dd_product_set',
  render: props => (
    <McpToolUI baseLabel="Product Set" result={props.result} toolCallId={props.toolCallId} defaultExpanded={true} />
  ),
});

const PublishDdProductSetToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'publish_dd_product_set',
  render: props => (
    <McpToolUI baseLabel="Company" result={props.result} toolCallId={props.toolCallId} defaultExpanded={true} />
  ),
});

const UpdateDdProductSetToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'update_dd_product_set',
  render: props => (
    <McpToolUI baseLabel="Product Set" result={props.result} toolCallId={props.toolCallId} defaultExpanded={true} />
  ),
});

// PLT-7013 — a set saved from uploaded files returns the same payload as `get_dd_product_set`, so
// it renders the same card.
const SaveDdProductSetFromFilesToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'save_dd_product_set_from_files',
  render: props => (
    <McpToolUI baseLabel="Product Set" result={props.result} toolCallId={props.toolCallId} defaultExpanded={true} />
  ),
});

// PLT-5523 / PLT-5520 — fund review + confirm card. The backend tags the
// read-only `review_staged_fund` result with `_ui: { appName: 'fund-review' }`
// (derived from `mcpAppResourceUri` in registry.ts); this registration routes it
// through `McpToolUI` so the iframe app mounts. Kept expanded — the coverage
// banner and holdings table are the whole point of the card, and its Confirm
// button is the user's next action, so it shouldn't collapse in a stack.
const ReviewStagedFundToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'review_staged_fund',
  render: props => (
    <McpToolUI baseLabel="Fund Review" result={props.result} toolCallId={props.toolCallId} defaultExpanded={true} />
  ),
});

// PLT-5583 (A2) — the before/after review card for adding a new dated time-version
// to an existing fund. Reuses the same `fund-review` iframe app as create (its
// payload is the create card shape plus per-row bucket metadata), routed via the
// tool's `mcpAppResourceUri` in registry.ts.
const ReviewFundVersionToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'review_fund_version',
  render: props => (
    <McpToolUI
      baseLabel="Fund Version Review"
      result={props.result}
      toolCallId={props.toolCallId}
      defaultExpanded={true}
    />
  ),
});

// ---------------------------------------------------------------------------
// PLT-4891 — agentic financial-statement input.
//
// `extract_financial_statements` returns a `card` payload and the backend tags
// the result with `_ui: { appName: 'financial-statement-import' }` (registry.ts
// mcpAppResourceUri). Without this registration the chat client falls back to
// the default text-only render and the editable review card never mounts — so
// this is what makes the inline card the primary review surface in web chat
// (PLT-5076). `defaultExpanded` so the draft table is visible immediately in
// narrow surfaces (the card IS the answer; there is no prose primitive that
// carries the headline when collapsed). `revise_draft` / `publish_draft` have no
// app of their own and intentionally have no UI here — the open extract card is
// the single review surface; their outcomes surface in the chat thread.
// ---------------------------------------------------------------------------
const FinancialStatementOfferToolUI = makeAssistantToolUI<
  Record<string, unknown>,
  { data?: FinancialStatementOffer | { dataAvailable: false } }
>({
  toolName: 'offer_financial_statements',
  render: ({ result }) => {
    if (!result?.data || 'dataAvailable' in result.data) return null;
    return <FinancialStatementOfferCard data={result.data} />;
  },
});

function FinancialStatementDraftContent({
  result,
  toolCallId,
}: {
  result: ToolResultWithUi | undefined;
  toolCallId: string;
}) {
  const data = result?.data as { card?: FinancialStatementImportPayload } | undefined;
  const card = data?.card;
  const draft = card?.status === 'review' ? card.draft : undefined;
  const isRunning = useThread(state => state.isRunning);
  const lastMessageId = useThread(state => state.messages.at(-1)?.id);
  // Read through the existing permission-checked route on reopen and after every turn.
  // A new turn key also retries after an interrupted or failed edit, without polling.
  const query = useQuery({
    queryKey: [FINANCIAL_STATEMENTS_QUERY_KEY, 'chat-draft', draft?.draftId, lastMessageId],
    queryFn: async () => {
      if (!draft) throw new Error('Statement draft is required');
      const statement = await fetchOrThrow<GetSingleCompanyFinancialStatementResponse>(
        `/company-financial-statement/${draft.draftId}`,
        'GET',
      );
      if (!statement.inputValues) throw new Error('Stored statement inputs are unavailable');
      return { ...statement, inputValues: statement.inputValues };
    },
    enabled: !!draft && !isRunning,
    placeholderData: keepPreviousData,
    staleTime: 0,
    retry: false,
  });
  const { refetch } = query;
  useEffect(() => {
    if (draft && !isRunning) {
      // The assistant message can exist before the run starts. An early read of that key
      // must never satisfy the post-turn refresh after revise_draft has actually finished.
      void refetch({ cancelRefetch: true });
    }
  }, [draft, isRunning, lastMessageId, refetch]);
  const refreshed = useMemo(() => {
    if (!result || !draft || card?.status !== 'review') return result;
    const current = query.data ? refreshFinancialStatementDraft(draft, query.data) : draft;
    const syncStatus = query.isError ? 'error' : isRunning || query.isFetching || !query.data ? 'pending' : 'ready';
    return {
      ...result,
      data: {
        ...data,
        card: {
          ...card,
          draft: {
            ...current,
            syncStatus,
            syncVersion: query.isPlaceholderData ? undefined : query.dataUpdatedAt || undefined,
          },
          questions: card.questions?.filter(
            question => !question.cellKey || current.cells[question.cellKey] === draft.cells[question.cellKey],
          ),
        },
      },
    };
  }, [
    result,
    data,
    card,
    draft,
    query.data,
    query.dataUpdatedAt,
    query.isPlaceholderData,
    query.isError,
    query.isFetching,
    isRunning,
  ]);
  return (
    <McpToolUI
      baseLabel="Financial statement draft"
      result={refreshed}
      toolCallId={toolCallId}
      defaultExpanded={true}
    />
  );
}

const ExtractFinancialStatementsToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'extract_financial_statements',
  render: props => <FinancialStatementDraftContent result={props.result} toolCallId={props.toolCallId} />,
});

// `publish_draft` statuses that mean the statement is now saved on the company. A failed
// recompute still leaves it saved, so the page has new data either way.
const SAVED_PUBLISH_STATUSES: ReadonlySet<string> = new Set([
  'published',
  'saved-before-first-results',
  'recompute-failed',
]);

/**
 * Refreshes the financial statements page after `publish_draft` saved a statement. Draws nothing:
 * the tool trail says whether it saved (PLT-7912).
 *
 * The draft card saves through the agent rather than through the platform app, so nothing else
 * tells the page a statement changed: with it mounted next to the chat it would keep showing what
 * it loaded before the save. This clears the same queries the page's own save does, the way the
 * operational-data card does for its page. A reopened chat has the call without its result, so it
 * does not run again.
 */
function PublishDraftRefresh({ result }: { result: ToolResultWithUi | undefined }) {
  const queryClient = useQueryClient();
  const data = (result?.data ?? result) as { status?: string } | undefined;
  const saved = typeof data?.status === 'string' && SAVED_PUBLISH_STATUSES.has(data.status);

  useEffect(() => {
    if (!saved) return;
    void queryClient.invalidateQueries({ queryKey: [FINANCIAL_STATEMENTS_QUERY_KEY] });
    void queryClient.invalidateQueries({ queryKey: [HAS_UNINTEGRATED_FINANCIAL_STATEMENT_CHANGES_QUERY_KEY] });
    void queryClient.invalidateQueries({ queryKey: ['companyVariant'] });
    void queryClient.invalidateQueries({ queryKey: ['materiality'] });
  }, [saved, queryClient]);

  return null;
}

const PublishDraftToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'publish_draft',
  render: ({ result }) => <PublishDraftRefresh result={result} />,
});

const CompareReleasesToolUI = makeAssistantToolUI<
  { companyId: string; releaseA: string; releaseB: string },
  ToolResultWithUi
>({
  toolName: 'compare_releases',
  render: () => null,
});

/** A sandbox-generated image, delivered inline as a base64 data URL (PLT-5030). */
interface CodeExecutionImage {
  dataUrl: string;
  mimeType: string;
  filename: string;
}

/**
 * Code execution tool. The code and what it printed open from its row in the tool trail
 * (PLT-7912); the images it generated are part of the answer, so they stay in the message.
 */
const CodeExecutionToolUI = makeAssistantToolUI<
  { code: string },
  { data: { stdout: string; stderr: string; images?: CodeExecutionImage[] } } | undefined
>({
  toolName: 'code_execution',
  render: ({ result }) => {
    // PLT-5030: files the sandbox generated (e.g. a matplotlib PNG), downloaded
    // server-side and forwarded as data URLs. Guard the shape — results
    // persisted before this shipped have no `images` field.
    const images = result?.data.images;
    const renderableImages = Array.isArray(images) ? images.filter(img => !!img.dataUrl) : [];
    if (renderableImages.length === 0) return null;
    return (
      <>
        {renderableImages.map((img, i) => (
          <img
            key={i}
            src={img.dataUrl}
            alt={img.filename || 'Generated image'}
            className="tool-result-image"
            style={{
              display: 'block',
              maxWidth: '100%',
              height: 'auto',
              borderRadius: '4px',
              marginTop: i === 0 ? 0 : '8px',
            }}
          />
        ))}
      </>
    );
  },
});

// PLT-5453: EU-mode `execute_python` — the custom AgentCore-backed code tool
// used on the Bedrock path (Anthropic's server `code_execution` doesn't exist
// there). It generates no images, so its code and output are in the tool trail alone.
const ExecutePythonToolUI = makeAssistantToolUI<
  { code: string },
  ToolResultWithUi | { stdout?: string; stderr?: string; message?: string }
>({
  toolName: 'execute_python',
  render: () => null,
});

// ---------------------------------------------------------------------------
// Citations — inline pills rendered mid-text via a remark plugin.
//
// The chat backend (`stream-events.ts`) splices `<cite id="<id>"/>` markers into
// streamed assistant text and emits a parallel `citation-meta` SSE event for
// each citation. The adapter (`upright-chat-adapter.ts`) writes those events
// into a module-level `citation-registry`. The `remark-citation-pills` plugin
// resolves each marker into a `link` mdast node with a `cite:<id>` URL; the
// `MarkdownText`'s `components.a` override then renders `CitationPillById`
// for those URLs (everything else is a regular anchor).
//
// The pill itself reveals a popover on hover/focus with the source title,
// URL, and exact quoted text. Click opens the source in a new tab; non-
// clickable citations (uploaded PDFs, unresolvable internal URIs) render the
// pill without a link but still surface the popover.
//
// Replaces the old "Sources (N)" collapsed details block — the value of
// citations is the click-through and the quote, not a header that makes
// users open a panel before they see anything. The previous iteration of
// this PR rendered a pill row directly under each paragraph; that produced
// a forced paragraph break before/after the row (the assistant_citations
// content part rendered as a sibling of the closed `<p>`). Inline markers
// avoid that and also handle Anthropic-style mid-paragraph citations.
// ---------------------------------------------------------------------------

/** Hosts whose citations are styled as Upright-internal sources (lilac). */
const UPRIGHT_HOST_PATTERN = /(^|\.)uprightplatform\.com$|(^|\.)uprightproject\.com$/i;

/**
 * Format a page range as "p. 5" / "pp. 5–8". Uses en-dash to match the
 * typographic conventions already used elsewhere in the chat UI.
 */
function formatPageRange(start: number, end: number): string {
  if (start === end) return `p. ${start}`;
  return `pp. ${start}–${end}`;
}

/**
 * Turn an uploaded-document filename like
 * `sustainability-report-2021-equinor.pdf` into a readable
 * "Sustainability Report 2021 Equinor" for display in the popover and
 * sidenote. We don't have access to the PDF's own title metadata on the
 * client (Anthropic returns the upload filename as `document_title`); a
 * proper fix would extract the embedded `/Title` at upload time on the
 * server. Until then, this cheap normalisation is a big readability win
 * over the raw filename.
 *
 * Rules:
 *   - strip a trailing `.pdf` / `.docx` / `.doc` / `.txt` / `.md` / `.html`
 *   - replace `_` and `-` runs with single spaces
 *   - collapse repeated whitespace
 *   - title-case each word, but preserve all-uppercase tokens (e.g. "GHG",
 *     "CSRD", "ESG") that read better as acronyms
 *
 * If the input doesn't look like a filename (no extension, no separators)
 * we leave it alone — the model may have been given a real title via the
 * upload metadata path.
 */
function prettifyDocumentTitle(title: string): string {
  const stripped = title.replace(/\.(pdf|docx?|txt|md|html?)$/i, '');
  const looksLikeFilename = stripped !== title || /[-_]/.test(stripped);
  if (!looksLikeFilename) return title;
  const spaced = stripped.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (spaced.length === 0) return title;
  return spaced
    .split(' ')
    .map(word => {
      // Preserve all-uppercase short tokens as acronyms; otherwise capitalise
      // the first letter and lowercase the rest.
      if (word.length <= 4 && word === word.toUpperCase() && /[A-Z]/.test(word)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

/**
 * Stable identifier for a citation's *source* (independent of which span
 * within that source is cited). Used by the sidenote renderer to detect
 * runs of citations that all point at the same document/page/url, so the
 * second and later sidenotes in such a run can be rendered without
 * repeating the title.
 *
 * Same-PDF citations from different page ranges still collapse — we
 * deliberately key on the document title alone, not the page range, so a
 * run of citations across pages 8, 9, and 12 of the same PDF reads as
 * "<title> · pp. 8–9 / p. 9 / p. 12" rather than three full-title blocks.
 */
function getCitationSourceKey(citation: ClientCitation): string {
  switch (citation.type) {
    case 'page_location':
    case 'char_location':
    case 'content_block_location':
      return `doc:${citation.document_title ?? ''}`;
    case 'web_search_result_location':
      return `web:${citation.url}`;
    case 'search_result_location':
      return `search:${citation.resolvedUrl ?? citation.source}`;
  }
}

/**
 * Normalised display info for a single citation, derived from the variant-
 * specific shape. Centralising the per-variant logic here lets the pill
 * renderer be type-agnostic.
 */
interface CitationDisplay {
  /** Short label shown inside the pill (host name, doc title, or "Upright"). */
  pillLabel: string;
  /** Full title shown bold at the top of the popover. */
  popoverTitle: string;
  /**
   * PLT-5025 — the quote to show under the title in the popover / sidenote /
   * Sources list, or `null` to show none. For PDF and web-search citations
   * this is the genuine `cited_text` Anthropic grounded on (real document /
   * webpage prose). For Upright-data (`search_result_location`) citations the
   * content block the model grounds on is often compact JSON, so `cited_text`
   * would be a raw JSON span — we surface it only when it reads as human prose
   * (a KB excerpt, an article insight) and suppress it otherwise. The
   * meaningful artifact for a data citation is its title + link, not a quote.
   */
  quote: string | null;
  /** Optional secondary line in the popover (URL, page range, etc.). */
  popoverSubtitle: string | null;
  /**
   * Optional metadata line shown in the sidenote on its own row (e.g.
   * "pp. 8–9"). Distinct from `popoverSubtitle` because the popover happily
   * shows verbose info like the full URL, but the 220px sidenote column
   * doesn't — we only surface compact, scannable metadata there.
   */
  sidenoteMeta: string | null;
  /** href to navigate to on click — null means non-clickable. */
  href: string | null;
  /** True for citations that resolve to upright-controlled domains. */
  isUprightSource: boolean;
  /**
   * PLT-4946 — unified source taxonomy. Every citation, regardless of which
   * Anthropic variant produced it, normalises to one of three origins so the
   * renderer can branch on a single field instead of the variant union:
   *   - `document` — uploaded PDF / file citation.
   *   - `web` — `web_search` result citation.
   *   - `upright` — Upright internal data / knowledge-base / platform link
   *     (the new `search_result` path from Phase 1, plus internal-kb URIs).
   * This is the field the unified layout (Phase 3) and the design-reuse
   * affordances (Upright bars mark, private badge) key off.
   */
  sourceType: 'document' | 'web' | 'upright';
  /**
   * PLT-4946 — true when an `upright` citation resolves to a private /
   * organization-tailored variant. Inferred from the company id in the source
   * URL via the shared `visibility-registry` (same mechanism the platform-link
   * sidenote uses). Drives the existing private badge in the rail.
   */
  isPrivate: boolean;
  /** Owner-org name for the private-badge tooltip, when known. */
  privateOwnerOrganizationName?: string;
}

/**
 * Try to parse a URL and extract a human-friendly host (no `www.` prefix).
 * Returns `null` for non-URL values so callers can fall back to a generic
 * label rather than rendering a broken pill.
 */
function tryParseHost(maybeUrl: string): string | null {
  try {
    const parsed = new URL(maybeUrl);
    return parsed.hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

/**
 * PLT-4946 — infer whether an Upright source URL points at a private /
 * organization-tailored variant, by parsing the `companyId` out of a
 * `/company/<id>/…` URL and looking it up in the shared visibility registry
 * (populated as tool results stream in — same bridge the platform-link
 * sidenote uses). Returns `{ isPrivate: false }` when the URL isn't company-
 * scoped or the registry hasn't seen the company yet — never a stale wrong
 * badge, matching `PlatformLinkPill`'s best-effort contract.
 */
function inferUprightPrivacy(href: string | null): { isPrivate: boolean; ownerOrganizationName?: string } {
  if (href === null) return { isPrivate: false };
  const companyId = tryParsePlatformCompanyId(href);
  if (companyId === null) return { isPrivate: false };
  const entry = getCompanyVisibility(companyId);
  if (entry?.visibility !== 'private') return { isPrivate: false };
  return entry.ownerOrganizationName
    ? { isPrivate: true, ownerOrganizationName: entry.ownerOrganizationName }
    : { isPrivate: true };
}

/**
 * PLT-5025 — heuristic: does this cited span look like a raw JSON fragment
 * rather than human prose? Upright-data citations ground on `search_result`
 * content blocks whose payload is often compact `JSON.stringify` output, so
 * `cited_text` can come back as `{"companyId":"…","netScore":0.3}` or a
 * `"key": value` fragment. We never want that in the UI. The backend emits
 * human prose (KB excerpts, article insights) as its own leading content block
 * so genuine snippets ground on prose and pass this filter; everything that
 * opens like an object/array/quoted-key is treated as JSON and suppressed.
 */
function looksLikeJsonSpan(text: string): boolean {
  const trimmed = text.trimStart();
  // Opens an object/array, or is a `"key":` / `key:` JSON property fragment.
  return /^[{}[\]]/.test(trimmed) || /^"[^"]*"\s*:/.test(trimmed) || /^[A-Za-z_$][\w$]*\s*:\s*[{["\d-]/.test(trimmed);
}

/** Compute display info for a single citation, regardless of its variant. */
function getCitationDisplay(citation: ClientCitation): CitationDisplay {
  switch (citation.type) {
    case 'page_location': {
      const rawTitle = citation.document_title ?? 'Uploaded document';
      const title = prettifyDocumentTitle(rawTitle);
      const range = formatPageRange(citation.start_page_number, citation.end_page_number);
      return {
        pillLabel: title,
        popoverTitle: title,
        popoverSubtitle: range,
        quote: citation.cited_text,
        sidenoteMeta: range,
        href: null,
        isUprightSource: false,
        sourceType: 'document',
        isPrivate: false,
      };
    }
    case 'char_location':
    case 'content_block_location': {
      const rawTitle = citation.document_title ?? 'Uploaded document';
      const title = prettifyDocumentTitle(rawTitle);
      return {
        pillLabel: title,
        popoverTitle: title,
        popoverSubtitle: null,
        quote: citation.cited_text,
        sidenoteMeta: null,
        href: null,
        isUprightSource: false,
        sourceType: 'document',
        isPrivate: false,
      };
    }
    case 'web_search_result_location': {
      const host = tryParseHost(citation.url);
      const title = citation.title ?? host ?? citation.url;
      const isUpright = host !== null && UPRIGHT_HOST_PATTERN.test(host);
      const privacy = isUpright ? inferUprightPrivacy(citation.url) : { isPrivate: false };
      return {
        pillLabel: isUpright ? 'Upright' : (host ?? title),
        popoverTitle: title,
        popoverSubtitle: citation.url,
        quote: citation.cited_text,
        sidenoteMeta: null,
        href: citation.url,
        isUprightSource: isUpright,
        // A web-search hit on an upright domain is still Upright-owned data, so
        // classify by ownership, not by which Anthropic variant produced it.
        sourceType: isUpright ? 'upright' : 'web',
        isPrivate: privacy.isPrivate,
        privateOwnerOrganizationName: privacy.ownerOrganizationName,
      };
    }
    case 'search_result_location': {
      // `source` may be `internal-kb://…` (no clickable target unless the
      // sanitizer resolved it to `resolvedUrl`) or a real `https://…` URL.
      const href = citation.resolvedUrl;
      const host = href !== null ? tryParseHost(href) : null;
      const title = citation.title ?? host ?? citation.source;
      // Internal-kb URIs and uprightplatform/docs hosts are both Upright sources.
      const isUpright =
        /^internal-kb:\/\//i.test(citation.source) || (host !== null && UPRIGHT_HOST_PATTERN.test(host));
      const privacy = isUpright ? inferUprightPrivacy(href) : { isPrivate: false };
      return {
        pillLabel: isUpright ? 'Upright' : (host ?? title),
        popoverTitle: title,
        popoverSubtitle: href ?? citation.source,
        // PLT-5025: suppress raw-JSON spans; surface only genuine prose snippets.
        quote: citation.cited_text && !looksLikeJsonSpan(citation.cited_text) ? citation.cited_text : null,
        sidenoteMeta: null,
        href,
        isUprightSource: isUpright,
        // search_result is the Upright internal-data path (Phase 1) — classify
        // as `upright` when the source resolves to an upright domain / kb URI,
        // otherwise treat it as a generic web source.
        sourceType: isUpright ? 'upright' : 'web',
        isPrivate: privacy.isPrivate,
        privateOwnerOrganizationName: privacy.ownerOrganizationName,
      };
    }
  }
}

/**
 * Inline citation — Tufte/Bret-Victor-inspired marginalia.
 *
 * Each citation renders two siblings:
 *   1. An *inline mark* — a tiny superscript-style number anchored where the
 *      citation appears in the assistant prose. This is the only thing that
 *      participates in the inline text flow, so it never breaks tables or
 *      forces line wraps the way the previous chip-with-filename did.
 *   2. A *sidenote* — the same number + source title + quoted snippet,
 *      floated into the right margin via `float: right` with a negative right
 *      margin (see `.chat-citation-sidenote` in `upright-chat-theme.css`).
 *      The sidenote escapes the prose column on wide viewports and is hidden
 *      on narrow viewports / when the report panel is open. The popover on
 *      the inline mark remains the fallback affordance for those cases.
 *
 * Click on the mark opens the source in a new tab; non-clickable citations
 * (uploaded PDFs, unresolvable internal URIs) render the mark as a plain
 * span with the popover still available.
 *
 * Numbering: the server-minted `index` is the running count across the whole
 * stream in arrival order. The Upright-vs-external distinction is conveyed
 * by colour (lilac vs neutral) — we stopped substituting "U" for the number
 * on Upright sources because the colour is enough and a stable number per
 * source reads better in the margin column.
 */
function CitationPill({
  citation,
  index,
  isContinuation = false,
}: {
  citation: ClientCitation;
  index: number;
  /**
   * True when the immediately preceding citation (by running index) points
   * to the same source as this one. The sidenote then renders as a compact
   * continuation entry — number + meta + quote, without repeating the title
   * — so a run of citations from one PDF doesn't fill the rail with the
   * same filename over and over.
   */
  isContinuation?: boolean;
}) {
  const display = getCitationDisplay(citation);
  const number = index + 1;
  const variantClass = display.isUprightSource ? '--upright' : '--external';

  const markClassName = [
    'chat-citation-mark',
    `chat-citation-mark${variantClass}`,
    display.href === null ? 'chat-citation-mark--inert' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const sidenoteClassName = [
    'chat-citation-sidenote',
    `chat-citation-sidenote${variantClass}`,
    isContinuation ? 'chat-citation-sidenote--continuation' : '',
    display.href !== null ? 'chat-citation-sidenote--clickable' : '',
    // PLT-4946: Upright-source citations carry the bars mark instead of the
    // number (matching the platform-link sidenote), and private/tailored
    // variants get the lock-badge styling — both reuse the existing CSS
    // modifiers from PLT-4524 / PLT-4548 so the rail reads as one column.
    display.sourceType === 'upright' ? 'chat-citation-sidenote--platform-link' : '',
    display.isPrivate ? 'chat-citation-sidenote--private' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // PLT-4946: private-variant badge tooltip — same wording as the platform-
  // link sidenote so the same chip reads identically wherever it appears.
  const privateTooltip = !display.isPrivate
    ? undefined
    : display.privateOwnerOrganizationName
      ? `Private — ${display.privateOwnerOrganizationName}'s data in Upright. Visible only to users in your organization.`
      : 'Private — your organization’s data in Upright. Visible only to users in your organization.';

  // Hover/focus popover — preserved from the previous pill design so it still
  // works on narrow viewports (where the sidenote is hidden) and as a quick
  // confirmation hover even when the sidenote is visible.
  const popover = (
    <span className="chat-citation-popover" role="tooltip">
      <span className="chat-citation-popover-title">{display.popoverTitle}</span>
      {display.popoverSubtitle !== null && (
        <span className="chat-citation-popover-subtitle">{display.popoverSubtitle}</span>
      )}
      {display.quote && <span className="chat-citation-popover-quote">&ldquo;{display.quote}&rdquo;</span>}
    </span>
  );

  const markContent = (
    <>
      <span className="chat-citation-mark-num">{number}</span>
      {popover}
    </>
  );

  const mark =
    display.href !== null ? (
      <a
        href={display.href}
        target="_blank"
        rel="noopener noreferrer"
        className={markClassName}
        title={display.popoverTitle}
      >
        {markContent}
      </a>
    ) : (
      <span className={markClassName} tabIndex={0} title={display.popoverTitle}>
        {markContent}
      </span>
    );

  // Sidenote: floated into the right margin via CSS. `aria-hidden` because
  // the inline mark is already the announceable, focusable affordance and
  // we don't want screen readers to read every source twice.
  //
  // Continuation entries hide the title via CSS (the
  // `chat-citation-sidenote--continuation` class) so a run of citations
  // from the same PDF reads as one labelled entry followed by compact
  // meta-only follow-ups, instead of three identical filenames stacked.
  //
  // When the citation has a URL (web sources, resolved internal-kb URIs,
  // platform deep links surfaced via search_result_location), we render the
  // whole sidenote as an `<a>` so the visible margin entry is the click
  // target — matching what users intuitively reach for. `tabIndex={-1}`
  // keeps it out of the tab order: the inline mark is the keyboard-
  // accessible focusable, and tab-stopping on every sidenote would double
  // the focus traversals for citation-heavy answers. PDF-style citations
  // (no resolvable URL) render as a plain span — same visual layout, no
  // hover affordance.
  // PLT-4946: Upright-source citations lead with the small "bars" mark
  // (the logo symbol from PLT-4524) instead of the running number, so a mixed
  // rail of numbered external/document citations and Upright-data citations
  // reads as two distinct affordances — exactly as platform-link sidenotes do.
  // The number is still kept for the inline mark (above) so the inline → rail
  // pairing is unambiguous; only the rail glyph changes.
  const sidenoteGlyph =
    display.sourceType === 'upright' ? (
      <span className="chat-citation-sidenote-platform-mark" aria-hidden="true">
        <UprightLogoSymbolBlackWhite />
      </span>
    ) : (
      <span className="chat-citation-sidenote-num">{number}</span>
    );

  const sidenoteInner = (
    <>
      {sidenoteGlyph}
      <span className="chat-citation-sidenote-body">
        <span className="chat-citation-sidenote-title">
          {display.popoverTitle}
          {display.isPrivate && (
            <span
              className="chat-citation-sidenote-private-badge"
              title={privateTooltip}
              aria-label={privateTooltip}
              role="img"
            >
              <LockGlyph />
              <span className="chat-citation-sidenote-private-badge-label">Private</span>
            </span>
          )}
        </span>
        {display.sidenoteMeta !== null && <span className="chat-citation-sidenote-meta">{display.sidenoteMeta}</span>}
        {display.quote && <span className="chat-citation-sidenote-quote">&ldquo;{display.quote}&rdquo;</span>}
      </span>
    </>
  );

  const sidenote =
    display.href !== null ? (
      <a
        href={display.href}
        target="_blank"
        rel="noopener noreferrer"
        className={sidenoteClassName}
        aria-hidden="true"
        tabIndex={-1}
      >
        {sidenoteInner}
      </a>
    ) : (
      <span className={sidenoteClassName} aria-hidden="true">
        {sidenoteInner}
      </span>
    );

  return (
    <>
      {mark}
      {sidenote}
    </>
  );
}

/**
 * Resolve a `<cite id="<id>"/>` marker (post-remark, surfaced as a `cite:<id>`
 * link in the markdown) against the per-tab citation registry and render
 * the corresponding citation mark + margin sidenote.
 *
 * If the id isn't in the registry, render nothing — fail closed. This can
 * happen briefly during streaming if React re-renders a text part before the
 * adjacent `citation-meta` event has been processed, but the next streamed
 * yield will re-render with the mark in place. It can also happen if a user
 * types `<cite id="foo"/>` literally; we'd rather drop it silently than render a
 * broken mark.
 */
export function CitationPillById({ id }: { id: string }) {
  const entry = getCitation(id);
  if (!entry) return null;
  // Detect "continuation" — same source as the immediately preceding
  // citation in arrival order. The registry's running index lets us look
  // up the previous entry without threading state through the markdown
  // pipeline. If the previous index isn't in the registry (e.g. the user
  // started a new conversation, or rendering races with stream events),
  // we fail closed — render as a fresh, fully-labelled sidenote.
  const previousEntry = entry.index > 1 ? getCitationByIndex(entry.index - 1) : undefined;
  const isContinuation =
    previousEntry !== undefined &&
    getCitationSourceKey(previousEntry.citation) === getCitationSourceKey(entry.citation);
  // `CitationPill` shows `index + 1` (1-based) and the registry stores the
  // server-minted 1-based running counter, so we subtract 1 here to keep
  // `CitationPill` as the source of truth for the off-by-one.
  return <CitationPill citation={entry.citation} index={entry.index - 1} isContinuation={isContinuation} />;
}

// ---------------------------------------------------------------------------
// PLT-4946 — responsive "Sources" list rendered after the message.
//
// The Tufte-style right-margin sidenote rail (`CitationPill`) is the wide-
// viewport placement. When there's no room for a margin column — the report
// panel is open, or the viewport is narrow (≤1360px) — the rail is hidden by
// CSS and the inline mark's hover popover is the only affordance. That's fine
// on a true hover device but useless on touch / when scanning.
//
// This component is the narrow-mode counterpart: a numbered/bars "Sources"
// list docked at the end of the assistant message. It reads ONLY the unified
// citation list (resolved from this message's `<cite id>` markers against the
// shared registry) so its placement has zero source-specific branches — doc,
// web, and Upright citations render through one shape, exactly as in the rail.
// CSS shows it only in the narrow cases (the inverse of the rail
// breakpoints), so wide viewports keep the marginalia and never double up.
// ---------------------------------------------------------------------------

/** Extract `<cite id="…"/>` ids from a message's assistant text, in order. */
function extractCiteIdsFromText(text: string): string[] {
  const ids: string[] = [];
  const re = /<cite\b[^>]*\bid\s*=\s*["']?([A-Za-z0-9_-]+)/gi;
  for (let m = re.exec(text); m !== null; m = re.exec(text)) {
    ids.push(m[1]);
  }
  return ids;
}

/**
 * Resolve a message's cite markers into a deduped, ordered citation list.
 * Dedup is by source key (`getCitationSourceKey`) so a run of citations from
 * one PDF / company collapses to a single entry — the after-message list is a
 * reference index, not a per-claim repeat. Keeps the first registry `index`
 * seen for each source so the list numbering matches the inline marks.
 */
function useMessageCitations(): Array<{ id: string; entry: RegisteredCitation }> {
  // The concatenated assistant text across all text parts of this message.
  // `content` returns a fresh array each delta, so this recomputes as the
  // message streams and as `citation-meta` events populate the registry.
  const text = useMessage(s =>
    s.content
      .filter((p: any) => p.type === 'text')
      .map((p: any) => p.text ?? '')
      .join(''),
  );
  const seenSourceKeys = new Set<string>();
  const out: Array<{ id: string; entry: RegisteredCitation }> = [];
  for (const id of extractCiteIdsFromText(text)) {
    const entry = getCitation(id);
    if (!entry) continue;
    const key = getCitationSourceKey(entry.citation);
    if (seenSourceKeys.has(key)) continue;
    seenSourceKeys.add(key);
    out.push({ id, entry });
  }
  return out;
}

/** One row in the after-message Sources list. Mirrors the sidenote affordances. */
function SourceListItem({ citation, number }: { citation: ClientCitation; number: number }) {
  const display = getCitationDisplay(citation);
  const variantClass = display.isUprightSource ? '--upright' : '--external';
  const privateTooltip = !display.isPrivate
    ? undefined
    : display.privateOwnerOrganizationName
      ? `Private — ${display.privateOwnerOrganizationName}'s data in Upright. Visible only to users in your organization.`
      : 'Private — your organization’s data in Upright. Visible only to users in your organization.';

  const glyph =
    display.sourceType === 'upright' ? (
      <span className="chat-sources-item-mark" aria-hidden="true">
        <UprightLogoSymbolBlackWhite />
      </span>
    ) : (
      <span className={`chat-sources-item-num chat-sources-item-num${variantClass}`}>{number}</span>
    );

  const body = (
    <span className="chat-sources-item-body">
      <span className="chat-sources-item-title">
        {display.popoverTitle}
        {display.isPrivate && (
          <span
            className="chat-citation-sidenote-private-badge"
            title={privateTooltip}
            aria-label={privateTooltip}
            role="img"
          >
            <LockGlyph />
            <span className="chat-citation-sidenote-private-badge-label">Private</span>
          </span>
        )}
      </span>
      {display.sidenoteMeta !== null && <span className="chat-sources-item-meta">{display.sidenoteMeta}</span>}
    </span>
  );

  const className = `chat-sources-item chat-sources-item${variantClass}`;
  return display.href !== null ? (
    <a href={display.href} target="_blank" rel="noopener noreferrer" className={className}>
      {glyph}
      {body}
    </a>
  ) : (
    <span className={className}>
      {glyph}
      {body}
    </span>
  );
}

/**
 * After-message References list. Rendered in the assistant message footer on
 * all viewports (PLT-5096) — a compact reference index that coexists with the
 * margin rail on wide screens (the rail carries the per-claim quote on hover,
 * so this list intentionally omits the quote snippet to stay compact). Named
 * "References" to disambiguate from the model-authored "Sources:" prose footer
 * for multi-company answers. CSS class names keep the `chat-sources-*` prefix.
 * Renders nothing when the message has no resolvable citations.
 */
export function MessageSources() {
  const citations = useMessageCitations();
  if (citations.length === 0) return null;
  return (
    <aside className="chat-sources" aria-label="References">
      <div className="chat-sources-heading">References</div>
      <div className="chat-sources-list">
        {citations.map(({ id, entry }) => (
          <SourceListItem key={id} citation={entry.citation} number={entry.index} />
        ))}
      </div>
    </aside>
  );
}

// Web search: the search and the sources it found are a row in the tool trail (PLT-7912). A failed
// search (EU mode: ScrapingBee 5xx/timeout, PLT-5456) reads "Couldn't search the web" there, so
// it no longer looks the same as a search that found nothing.
const WebSearchToolUI = makeAssistantToolUI<
  { query: string },
  ToolResultWithUi | { citations: Array<{ title: string; url: string }> }
>({
  toolName: 'web_search',
  render: () => null,
});

/** Style guide tool: its row in the tool trail is all it shows. */
const StyleGuideToolUI = makeAssistantToolUI({
  toolName: 'get_style_guide',
  render: () => null,
});

/**
 * Flatten a `react-markdown` link's children into a plain string for the
 * sidenote title. Markdown link labels come in as a small subtree because
 * they may contain inline emphasis (`*Storebrand*`), so we walk the React
 * nodes and concatenate their text content. Same shape that
 * `react-children-to-text` packages do — duplicated here to avoid pulling
 * in another dep just for one helper.
 *
 * Returns the empty string when the children are exotic (`null`, fragments
 * with no text, etc.); callers fall back to the URL host so an empty title
 * never reaches the rail.
 */
function reactChildrenToText(children: ReactNode): string {
  if (children === null || children === undefined || typeof children === 'boolean') return '';
  if (typeof children === 'string') return children;
  if (typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(reactChildrenToText).join('');
  if (typeof children === 'object' && 'props' in children) {
    const inner = (children as { props?: { children?: ReactNode } }).props?.children;
    return inner !== undefined ? reactChildrenToText(inner) : '';
  }
  return '';
}

/**
 * Sidenote-style margin entry for an Upright Platform deep-link the model
 * embedded as a markdown link in chat prose.
 *
 * Why a separate component from `CitationPill`:
 *
 *   - These are NOT Anthropic citations — they're tool-result `url` fields
 *     surfaced by the system-prompt nudge, and they never enter the
 *     citation registry. They have no stable id and no running index, so
 *     the registry-lookup path in `CitationPillById` doesn't apply.
 *   - Numbering them alongside numbered citations would imply they are
 *     evidence the same way (a quoted source), which they're not — they
 *     are "view this on the platform" navigation links. We mark them with
 *     a small Upright bars logo instead so a mixed rail (numbered citations
 *     + platform links) reads as two distinct affordances rather than a
 *     confusing mixed-numbering scheme; the logo also reinforces that the
 *     link goes back to uprightplatform.com.
 *   - The inline anchor stays in the prose (the link text is the user's
 *     reading affordance); the margin sidenote is the additional rail
 *     entry that pairs with it for the Tufte-style two-column layout.
 *
 * Both surfaces point at the same `href`. The margin sidenote piggybacks
 * on the existing `.chat-citation-sidenote--upright` lilac styling so the
 * rail reads as one consistent column even when platform links and
 * numbered Upright citations interleave.
 */
export function PlatformLinkPill({ href, children }: { href: string; children: ReactNode }) {
  const host = tryParsePlatformLinkHost(href);
  // Title prefers the visible link text (e.g. "Storebrand DMA"), falling
  // back to the host so a sidenote without a usable label still reads
  // "uprightplatform.com" rather than going blank.
  const titleText = reactChildrenToText(children).trim() || host || href;

  // PLT-4548: when this platform link points at a `/company/<id>/...` URL and
  // the chat has already seen the corresponding tool result, surface the
  // visibility marker on the right-margin sidenote so the citation rail
  // matches the badge on the MCP-app card. Lookup is best-effort: the
  // registry is populated from tool-result render passes (see
  // `registerVisibilityFromToolResult`); during the first frame of streaming
  // a markdown link can render before the matching tool result has come back,
  // in which case we render no badge — same outcome as a public link, never
  // a stale wrong-badge.
  const companyId = tryParsePlatformCompanyId(href);
  const visibilityEntry = companyId ? getCompanyVisibility(companyId) : undefined;
  const isPrivate = visibilityEntry?.visibility === 'private';
  // When isPrivate is true, visibilityEntry is by definition non-undefined, so
  // the inner ownerOrganizationName access is safe and doesn't need an extra
  // optional chain (which eslint rightly flags as redundant).
  const tooltip = !isPrivate
    ? undefined
    : visibilityEntry.ownerOrganizationName
      ? `Private — ${visibilityEntry.ownerOrganizationName}'s data in Upright. Visible only to users in your organization.`
      : 'Private — your organization’s data in Upright. Visible only to users in your organization.';

  return (
    <>
      <PlatformAnchor
        href={href}
        // `aui-md-a` matches assistant-ui's default markdown link class so
        // base typography stays consistent with non-platform links;
        // `chat-platform-link` carries the lilac tint that pairs the inline
        // affordance with the lilac rail entry.
        className="aui-md-a chat-platform-link"
      >
        {children}
      </PlatformAnchor>
      <PlatformAnchor
        href={href}
        // Reuses `--upright` colour styling and `--clickable` so the hover
        // rules in `upright-chat-theme.css` apply unchanged. The `--platform-link`
        // modifier scopes the bars-logo mark styling without leaking into the
        // numbered-citation column. `--private` adds the lock badge styling
        // (PLT-4548) when visibility-registry says the company id in this
        // URL was returned with `visibility: 'private'`.
        className={
          'chat-citation-sidenote chat-citation-sidenote--upright chat-citation-sidenote--clickable chat-citation-sidenote--platform-link' +
          (isPrivate ? ' chat-citation-sidenote--private' : '')
        }
        // Inline anchor is the focusable affordance for keyboard users; the
        // sidenote duplicates it for visual scanning, so we hide it from
        // assistive tech and the tab order to avoid double-announcing each
        // platform link.
        aria-hidden="true"
        tabIndex={-1}
      >
        <span className="chat-citation-sidenote-platform-mark" aria-hidden="true">
          <UprightLogoSymbolBlackWhite />
        </span>
        <span className="chat-citation-sidenote-body">
          <span className="chat-citation-sidenote-title">
            {titleText}
            {isPrivate && (
              <span className="chat-citation-sidenote-private-badge" title={tooltip} aria-label={tooltip} role="img">
                <LockGlyph />
                {/* Short "Private" label — the "your organization" qualifier
                    lives in the tooltip above. Keeping the visible badge
                    narrow prevents it from overflowing the company-name row
                    in the citation rail (PLT-4602). */}
                <span className="chat-citation-sidenote-private-badge-label">Private</span>
              </span>
            )}
          </span>
          {host !== null && <span className="chat-citation-sidenote-meta">{host}</span>}
        </span>
      </PlatformAnchor>
    </>
  );
}

/**
 * A platform link from a chat answer. In the unified shell, a link to a page of this app is an
 * in-app navigation in the same tab, so the page it opens records the chat as where it came from
 * and its Back returns there (PLT-7159); cmd- and middle-click still open a new tab. Elsewhere,
 * and for any other site, it opens a new tab as an off-site link does.
 */
function PlatformAnchor({
  href,
  children,
  ...rest
}: {
  href: string;
  children: ReactNode;
  className: string;
  'aria-hidden'?: 'true';
  tabIndex?: number;
}) {
  const unifiedShell = useUnifiedShell();
  const inAppPath = unifiedShell.enabled ? sameOriginPlatformPath(href) : null;
  if (inAppPath !== null) {
    return (
      <Link to={inAppPath} {...rest}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
      {children}
    </a>
  );
}

/**
 * Tiny inline lock SVG used inside `chat-citation-sidenote-private-badge`
 * and `chat-private-lock-badge`. Inline so the chat bundle has no extra
 * asset dependency; sized via the surrounding span's font-size and
 * currentColor for theming.
 */
function LockGlyph() {
  return (
    <svg width="10" height="10" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M5 5a3 3 0 0 1 6 0v2h.5A1.5 1.5 0 0 1 13 8.5v5A1.5 1.5 0 0 1 11.5 15h-7A1.5 1.5 0 0 1 3 13.5v-5A1.5 1.5 0 0 1 4.5 7H5V5zm5 2V5a2 2 0 1 0-4 0v2h4z"
      />
    </svg>
  );
}

/**
 * Inline private chip rendered in assistant prose by `remarkLockBadges`
 * (see `lock-badge-markers.ts`) wherever the LLM emits a `<private />`
 * marker — the first mention of a private (organization-tailored)
 * company in an answer.
 *
 * Replaces the bare 🔒 emoji previously emitted (PLT-4548) with a
 * lilac/purple pill carrying a lock glyph + "Private" label, matching
 * the right-rail citation sidenote private badge (post-PLT-4602) and
 * the MCP App card header badge — same chip style appears wherever a
 * private result surfaces. PLT-4617 added the "Private" label to the
 * inline form (previously icon-only) and moved the chip after the
 * company name in prose, so the inline reading matches the right-rail
 * sidenote layout (`**Acme** [lock Private]`).
 *
 * The hover tooltip and `aria-label` carry the longer "Private — your
 * organization" wording for screen readers and pointer-only users —
 * the surrounding prose no longer needs the parenthetical because the
 * chip itself reads as "Private".
 */
export function InlineLockBadge() {
  // Source-of-truth wording lives in the backend `visibility-labels.ts`;
  // we keep the same em-dash form here so the inline chip tooltip
  // matches the right-rail badge label and the MCP-card label exactly.
  // Manually kept in sync — same convention as the existing
  // `chat-citation-sidenote-private-badge-label` literal above.
  const tooltip = 'Private — your organization';
  return (
    <span className="chat-private-lock-badge" title={tooltip} aria-label={tooltip} role="img">
      <LockGlyph />
      {/* Short "Private" label — the "your organization" qualifier
          lives in the tooltip / `aria-label` above. Mirrors the
          right-rail sidenote badge so the same chip reads consistently
          wherever a private result surfaces (PLT-4617). */}
      <span className="chat-private-lock-badge-label">Private</span>
    </span>
  );
}

/**
 * How long after a chip is fired a repeat click on the SAME chip is ignored.
 * Only has to outlast the render that flips `isRunning` to true; short enough
 * that a deliberate second send of the same prompt still goes through.
 */
const CHIP_APPEND_DEDUP_MS = 2000;

/**
 * Bottom follow-up suggestion chips — rendered at the end of an assistant
 * message. The single follow-up channel after PLT-4590 (the inline
 * `[[fu:…]]` channel was removed and the no-overlap rule with it).
 *
 * Every assistant message that emitted the tool keeps its own live row, not
 * just the newest one — a deliberate product call (Janne, PLT-6119), so that a
 * reader can go back to an earlier answer and take a fork they skipped. Any
 * guard added here has to leave that intact: nothing may permanently retire a
 * row, and using one chip must not retire its siblings.
 *
 * A chip the user has sent is marked but stays clickable, and the mark is held
 * per tool call in `followup-used-registry.ts` rather than in this component, so
 * it survives the side-panel → full-chat remount. Both follow from the same
 * call: the row's value is that a reader can come back to it, which needs the
 * row to say which forks are already taken without closing any of them.
 */
export interface FollowupSuggestion {
  label: string;
  prompt: string;
  /** Send the user's latest uploads again with the prompt (PLT-7459); see `uploadsToResend`. */
  resendUploads?: boolean;
}

export interface SuggestFollowupsArgs {
  suggestions: FollowupSuggestion[];
}

function SuggestFollowupsContent({
  args,
  result,
  toolCallId,
}: {
  args: SuggestFollowupsArgs;
  result?: { data: unknown };
  toolCallId: string;
}) {
  const isRunning = useThread(state => state.isRunning);

  // Don't render until tool has completed, and only if we have suggestions
  if (!result || !args.suggestions.length) return null;

  return (
    <div className="chat-suggestion-chips">
      {/* PLT-5563 (Milja): a heading + a per-chip glyph so the pills read as clickable
          follow-up prompts in context, rather than "random" buttons whose behaviour is
          only discoverable by chancing on the hover tooltip. The full prompt still shows
          on hover via `title` (each chip's label is a short form of that prompt). */}
      <div className="chat-suggestion-chips-label">Suggested follow-ups</div>
      <div className="chat-suggestion-chips-row">
        {args.suggestions.map((s: FollowupSuggestion) => (
          <SuggestionChip key={s.prompt} suggestion={s} toolCallId={toolCallId} isRunning={isRunning} />
        ))}
      </div>
    </div>
  );
}

/**
 * One follow-up chip. A component of its own rather than JSX inside the row's
 * `map`, because each chip subscribes to its own used-mark and holds its own
 * dedup timestamp — both of which are per chip, and neither of which any
 * sibling should re-render for.
 */
function SuggestionChip({
  suggestion,
  toolCallId,
  isRunning,
}: {
  suggestion: FollowupSuggestion;
  toolCallId: string;
  isRunning: boolean;
}) {
  const runtime = useAssistantRuntime();
  // Whether the user has already sent this chip from this row. Read from the
  // module-level registry rather than component state so the mark survives the
  // side-panel → full-chat remount; see `followup-used-registry.ts`.
  const isUsed = useSyncExternalStore(subscribeFollowupsUsed, () => isFollowupUsed(toolCallId, suggestion.prompt));
  // Timestamp of this chip's last dispatch, so the chip refuses an immediate
  // repeat. `isRunning` cannot cover that on its own: it only flips true on a
  // later render, so two clicks dispatched in the same tick both read `false`,
  // and the second appends the prompt again — aborting the run the first one
  // just started (PLT-6093).
  //
  // A ref, not state: nothing renders differently for it. Per chip and
  // expiring, never shared across the row — a window shared across a whole row
  // also swallows the corrective click after a mis-click, which is the bug
  // PLT-6108 fixed in `SuggestedPromptCards`.
  const lastAppendAtRef = useRef(0);

  return (
    <button
      className="chat-suggestion-chip"
      // Faded only while a turn is streaming. A chip is a submit button and
      // a turn takes ~80s at the mean, over two minutes at p90, so without
      // a visible refusal a reader assumes the click missed and clicks
      // again — and `runtime.thread.append` would abort the answer being
      // streamed to pay for a fresh turn saying the same thing (PLT-6093).
      // The dedup window below needs no styling of its own: `isRunning` is
      // true for all but the first render of it.
      //
      // `aria-disabled` rather than the native `disabled`, which would take
      // the chip out of the tab order mid-turn — dropping a keyboard user's
      // focus to `<body>` — and suppress the `title` tooltip that is the
      // only place the full prompt is readable. The refusal lives in the
      // handler instead, and the styling keys off the same attribute.
      aria-disabled={isRunning}
      // Marks a chip the user has already sent. Deliberately not an inert
      // state: a reader who wants the same fork again after the thread moved
      // on has a legitimate reason to re-send it, so the mark says "you
      // already sent this" and never "you may not send this" (PLT-6117).
      data-used={isUsed ? '' : undefined}
      onClick={() => {
        if (isRunning) return;
        const now = Date.now();
        if (now - lastAppendAtRef.current < CHIP_APPEND_DEDUP_MS) return;
        lastAppendAtRef.current = now;
        markFollowupUsed(toolCallId, suggestion.prompt);
        composerState.pendingSource = 'followup_suggestion';
        // A chip whose action needs the uploaded files again carries them
        // (PLT-7459); otherwise the click starts a turn without them.
        const attachments = suggestion.resendUploads
          ? uploadsToResend(runtime.thread.getState().messages, toolCallId)
          : [];
        runtime.thread.append(
          attachments.length > 0
            ? { role: 'user', content: [{ type: 'text', text: suggestion.prompt }], attachments }
            : suggestion.prompt,
        );
      }}
      // The full prompt is only readable here, so the used-mark is appended to
      // it rather than replacing it — for a mouse user the tooltip is where the
      // mark gets its words. It is not what carries the mark to assistive tech:
      // a button with visible text takes its accessible name from that text, so
      // `title` demotes to the accessible *description*, which screen readers
      // announce inconsistently and touch users never see at all.
      title={isUsed ? `${suggestion.prompt} — already sent` : suggestion.prompt}
    >
      {/* The leading glyph says which of the two things the chip is: an info
          mark for a suggestion still on offer — it signals the pill carries more
          than its label, namely the full prompt in the `title` (PLT-5563) — and
          a check for one already sent (PLT-6117). Both are design-system
          Streamline icons in one box, so the swap changes the mark and nothing
          else. A pair drawn from two sources differs in stroke weight and ink
          extent as well, and the state change then reads partly as "the glyph
          got lighter" rather than only as "this one is taken". */}
      {isUsed ? (
        <CheckStreamlineCoreLine className="chat-suggestion-chip-glyph" aria-hidden="true" />
      ) : (
        <InfoCircle className="chat-suggestion-chip-glyph" aria-hidden="true" />
      )}
      <span className="chat-suggestion-chip-label">{suggestion.label}</span>
      {/* The used-mark, in the accessible name. The glyph is `aria-hidden` and
          the colour shift is invisible to a screen reader, so without this the
          state simply does not exist for one — the same fault the `aria-disabled`
          choice above was made to avoid. The visible label is deliberately left
          alone: it is what the reader picked the chip by, and re-wording it on
          use would make the row hard to re-read. */}
      {isUsed ? <VisuallyHidden>, already sent</VisuallyHidden> : null}
    </button>
  );
}

const SuggestFollowupsToolUI = makeAssistantToolUI<SuggestFollowupsArgs, { data: unknown }>({
  toolName: 'suggest_followups',
  render: ({ args, result, toolCallId }) => (
    <SuggestFollowupsContent args={args} result={result} toolCallId={toolCallId} />
  ),
});

// ---------------------------------------------------------------------------
// `present_headline_kpis` — chat-only tool for "stat tile row" leads.
//
// The server-side tool (in `mcp/tools/present-headline-kpis.ts`) echoes
// the tiles back as the data payload; this UI renders them with the
// shared `KpiTileRow` component, which picks the matching valence
// palette from `tokens.css`. Restricted-company handling is upstream —
// the data the tiles report came from data tools that already applied
// the guard.
//
// Args carry the same `tiles` array, but the structural source of truth
// is the result. We keep the args as a fallback for the streaming-pre-
// result frame so the UI can render an outline immediately when the
// model commits to the tool call.
// ---------------------------------------------------------------------------

export interface PresentHeadlineKpisArgs {
  tiles: Array<{
    label: string;
    value: string;
    sub?: string;
    tone: 'positive' | 'negative' | 'neutral';
  }>;
}

interface PresentHeadlineKpisPayload {
  tiles: Array<{
    label: string;
    value: string;
    sub?: string;
    tone: 'positive' | 'negative' | 'neutral';
  }>;
}

interface PresentHeadlineKpisResult {
  /** Either the validated tile row, or the `{error}` envelope a rejected call returns. */
  data: PresentHeadlineKpisPayload | { error: string; message?: string };
}

/** The `{error}` envelope the server returns for a rendering-tool call it refused. */
interface RejectedCardResult {
  error: string;
  message?: string;
}

/**
 * True when a rendering tool's result is that envelope rather than a card payload.
 *
 * Takes `unknown` and re-checks the shape at runtime instead of trusting the
 * declared result type. Live, this value is a `JSON.parse` of the tool result; on
 * the rehydration path it is whatever the backend persisted into the transcript's
 * `contentEvents` JSONB (PLT-6108). Neither is guaranteed to be an object, and a
 * bare `'error' in data` on a `null` or a primitive throws a TypeError that takes
 * the whole message subtree down with it (PLT-6113).
 */
function isRejectedCardResult(data: unknown): data is RejectedCardResult {
  return typeof data === 'object' && data !== null && 'error' in data;
}

/**
 * Render body of the `present_headline_kpis` tool UI. A component of its own,
 * like `SuggestFollowupsContent`, so its hook lives in a component React owns
 * rather than in whatever host is currently rendering the tool part.
 */
function PresentHeadlineKpisContent({
  args,
  result,
}: {
  args: PresentHeadlineKpisArgs;
  result?: PresentHeadlineKpisResult;
}) {
  // Message-scoped, NOT thread-scoped (PLT-6113). `useThread(s => s.isRunning)`
  // is true whenever *any* run on the thread is live, and the tool-call part of
  // a dead turn is never mutated — so a thread-scoped predicate hid the stranded
  // row while the thread was idle and then brought it back inside the finished
  // answer on every later turn. `status?.type === 'running'` is true only while
  // this message is streaming, and is 'complete'/'incomplete' for finished,
  // aborted, errored and rehydrated messages alike. Same predicate and same
  // reasoning as `SuggestFollowupsContent` above.
  const isMessageRunning = useMessage(s => s.status?.type === 'running');
  // A rejected call carries no `tiles`. Render nothing rather than falling
  // back to `args.tiles`, which is the unvalidated payload the server just
  // refused — a tile row is a lede the prose repeats anyway, so dropping it
  // beats drawing something validation rejected. Overshooting a tile cap
  // no longer lands here at all: the server trims text and row width first
  // (PLT-6093 / PLT-6108), so what remains is a substantive fault the model
  // must fix.
  const data = result?.data;
  if (isRejectedCardResult(data)) return null;

  // Prefer the server-validated `result.data.tiles` once available;
  // fall back to args during the brief streaming window before the
  // tool-result event arrives. (`args.tiles` is required by the
  // schema, but the streaming preview frame can arrive with the array
  // not yet populated, so still default to `[]`.)
  const tiles = (data?.tiles ?? args.tiles) as KpiTileProps[] | undefined;
  if (!tiles || tiles.length === 0) {
    // "Preparing" is only true while this message is still streaming. A run the
    // user stopped — or one that died — never delivers the result, and nothing
    // later clears this row, so it would sit in the finished answer as a
    // permanent spinner (PLT-6108). Same reasoning as the rejection branch
    // above: the tiles are a lede, so their absence costs the reader less than a
    // stuck placeholder.
    if (!isMessageRunning) return null;
    return (
      <div className="tool-status">
        <span className="tool-status-spinner" />
        Preparing tiles...
      </div>
    );
  }
  return <KpiTileRow tiles={tiles} />;
}

const PresentHeadlineKpisToolUI = makeAssistantToolUI<PresentHeadlineKpisArgs, PresentHeadlineKpisResult>({
  toolName: 'present_headline_kpis',
  render: props => <PresentHeadlineKpisContent {...props} />,
});

// ---------------------------------------------------------------------------
// `emit_callout` — chat-only tool that replaces the legacy
// `:::callout{tone="…"} … :::` inline directive (PLT-4590).
//
// The server-side tool (in `mcp/tools/emit-callout.ts`) echoes the
// validated `{ tone, title?, bodyMarkdown }` payload as the result data;
// this UI renders the `<Callout>` block and routes the body markdown
// through `ChatMarkdown` so inline `<badge>` / `<cite id="…"/>`
// directives, follow-up chips, and platform deep-links work the same
// way inside a callout as in the surrounding prose.
//
// Args carry the same fields as the result, but the structural source
// of truth is the result. We keep `args` as a fallback for the streaming
// pre-result frame so the UI can render an outline immediately when the
// model commits to the tool call.
// ---------------------------------------------------------------------------

export interface EmitCalloutArgs {
  tone: Tone;
  title?: string;
  bodyMarkdown: string;
}

interface EmitCalloutPayload {
  tone: Tone;
  title?: string;
  bodyMarkdown: string;
}

interface EmitCalloutResult {
  /** Either the validated callout, or the `{error}` envelope a rejected call returns. */
  data: EmitCalloutPayload | { error: string; message?: string };
}

/**
 * Render body of the `emit_callout` tool UI — a component of its own for the
 * same reason as `PresentHeadlineKpisContent` above.
 */
function EmitCalloutContent({ args, result }: { args: EmitCalloutArgs; result?: EmitCalloutResult }) {
  // Message-scoped for the same reason as `PresentHeadlineKpisContent` above
  // (PLT-6113) — a thread-scoped `isRunning` resurrects this placeholder inside
  // the finished answer whenever a later turn starts.
  const isMessageRunning = useMessage(s => s.status?.type === 'running');
  // A call the server rejected comes back as `{error: 'invalid_input', …}`
  // rather than the callout payload. Render nothing: a callout is a frame
  // around prose the model has already written, so dropping it costs the
  // reader nothing, and the model reissues a corrected call on the next
  // round. Falling through to the spinner below instead left a
  // "Preparing callout..." row sitting in the finished answer forever —
  // nothing later ever clears it (PLT-6093).
  const resultData = result?.data;
  if (isRejectedCardResult(resultData)) return null;

  // Prefer the server-validated result; fall back to args while the
  // tool call is still streaming.
  const data: EmitCalloutPayload | EmitCalloutArgs = resultData ?? args;
  const bodyMarkdown = typeof data.bodyMarkdown === 'string' ? data.bodyMarkdown : '';
  if (bodyMarkdown.length === 0) {
    // The same stuck row PLT-6093 removed on the rejection path reappears when
    // the result never arrives at all — the user presses Stop mid-stream, or the
    // run fails between the tool call and its result. Once this message has
    // stopped streaming there is nothing left to wait for, so drop the
    // placeholder (PLT-6108).
    if (!isMessageRunning) return null;
    return (
      <div className="tool-status">
        <span className="tool-status-spinner" />
        Preparing callout...
      </div>
    );
  }
  return (
    <Callout tone={coerceTone(data.tone)} title={typeof data.title === 'string' ? data.title : undefined}>
      <ChatMarkdown>{bodyMarkdown}</ChatMarkdown>
    </Callout>
  );
}

const EmitCalloutToolUI = makeAssistantToolUI<EmitCalloutArgs, EmitCalloutResult>({
  toolName: 'emit_callout',
  render: props => <EmitCalloutContent {...props} />,
});

// ---------------------------------------------------------------------------
// `propose_tailoring_batch` — chat-only sign-off card (PLT-4895).
//
// The server-side tool (`mcp/tools/propose-tailoring-batch.ts`) schema-
// validates and echoes the proposal back. This UI renders the validated
// payload as a `<TailoringBatchCard>`. The card owns its own selection
// state and POSTs directly to the batch-apply endpoint — that path is
// independent of assistant-ui, so the card is unaffected by message
// re-renders, switch-thread, etc., once it's mounted.
//
// `variantId` for the apply POST is resolved client-side from the
// company the agent named in the proposal. We deliberately do NOT pull
// the chat's currently-selected variant from a global store, because a
// chat session can outlive a company-page mount (the user can navigate
// between pages mid-conversation). Resolving from `companyId` keeps the
// card self-contained.
// ---------------------------------------------------------------------------

interface ProposeTailoringBatchData {
  companyId: string;
  proposalId: string;
  summaryMarkdown?: string;
  priorDmaLabel?: string;
  items: TailoringBatchCardProps['items'];
  citations?: TailoringBatchCardProps['citations'];
  // PLT-7899: on the server result only; the streamed args never carry it.
  ownInsightContext?: TailoringBatchCardProps['ownInsightContext'];
  // PLT-4968: when the user can't apply (no edit access / tailoring not enabled) the server returns
  // `dataAvailable: false` and no items — no card renders; the assistant's prose carries the message.
  dataAvailable?: false;
}

interface ProposeTailoringBatchResult {
  data: ProposeTailoringBatchData & { _interpretation?: unknown };
}

function ProposeTailoringBatchContent({ data }: { data: ProposeTailoringBatchData }) {
  // Resolve the company's default (originating) variantId from existing
  // score data — the backend handler will follow the 1:1 tailoring link
  // to the tailored variant. Returns `undefined` while loading or when
  // the user has no access to the company's variant; in either case the
  // card disables Apply and shows the "open from a company page" hint.
  const variantId = useGetCompanyVariantId(data.companyId);
  return (
    <TailoringBatchCard
      companyId={data.companyId}
      variantId={variantId ?? null}
      proposalId={data.proposalId}
      summaryMarkdown={data.summaryMarkdown}
      priorDmaLabel={data.priorDmaLabel}
      items={data.items}
      citations={data.citations}
      ownInsightContext={data.ownInsightContext}
    />
  );
}

const ProposeTailoringBatchToolUI = makeAssistantToolUI<ProposeTailoringBatchData, ProposeTailoringBatchResult>({
  toolName: 'propose_tailoring_batch',
  render: ({ args, result }) => {
    // A rejected call (`{error: 'invalid_input'}`) or a failed one renders nothing: the model
    // reissues a corrected call, and falling back to `args` would leave a "Preparing" spinner that
    // nothing clears, next to prose that may say the proposal is on screen (PLT-7741).
    if (isRejectedCardResult(result?.data)) return null;
    // Prefer the server-validated result; fall back to args during the streaming preview window.
    const data = (result?.data ?? args) as ProposeTailoringBatchData;
    // PLT-4968: apply-access denial — no card; the assistant's prose tells the user they lack edit
    // access and offers a read-only comparison. Render nothing rather than a stuck "preparing" spinner.
    if (result?.data && data.dataAvailable === false) {
      return null;
    }
    // Still streaming: the tool trail shows "Preparing tailoring proposal" (PLT-7912).
    if (!data.companyId || !Array.isArray(data.items) || data.items.length === 0) return null;
    return <ProposeTailoringBatchContent data={data} />;
  },
});

// ---------------------------------------------------------------------------
// `propose_operational_data_batch` — chat-only sign-off card (PLT-5088).
//
// The server-side tool (`mcp/tools/propose-operational-data-batch.ts`) schema-
// validates and echoes the proposal back. This UI renders the validated
// payload as an `<OpDataBatchCard>`. The card owns its own per-dataset state
// and POSTs each confirmed dataset directly to the batch-apply endpoint —
// independent of assistant-ui, so it survives message re-renders.
//
// `variantId` (the company variant the apply endpoint POSTs to) is resolved
// client-side from the company the agent named, exactly as the tailoring card
// does, so the card stays self-contained and we never surface a variant id.
// ---------------------------------------------------------------------------

interface ProposeOperationalDataBatchData {
  companyId: string;
  companyName?: string;
  proposalId: string;
  summaryMarkdown?: string;
  datasets: OpDataBatchCardProps['datasets'];
  sustainabilityIndicators?: OpDataBatchCardProps['sustainabilityIndicators'];
  // PLT-5088: when the user can't apply (no edit access) the server returns `dataAvailable: false`
  // and no datasets — no card renders; the assistant's prose carries the message.
  dataAvailable?: false;
}

interface ProposeOperationalDataBatchResult {
  data: ProposeOperationalDataBatchData & { _interpretation?: unknown };
}

function ProposeOperationalDataBatchContent({ data }: { data: ProposeOperationalDataBatchData }) {
  // Resolve the company's default variantId (the value the batch-apply endpoint takes in its path).
  // Returns `undefined` while loading or when the user has no access to the company's variant; in
  // either case the card disables saving and shows the "open from a company page" hint.
  const variantId = useGetCompanyVariantId(data.companyId);
  return (
    <OpDataBatchCard
      companyId={data.companyId}
      companyName={data.companyName}
      variantId={variantId ?? null}
      proposalId={data.proposalId}
      summaryMarkdown={data.summaryMarkdown}
      datasets={data.datasets}
      sustainabilityIndicators={data.sustainabilityIndicators}
    />
  );
}

const ProposeOperationalDataBatchToolUI = makeAssistantToolUI<
  ProposeOperationalDataBatchData,
  ProposeOperationalDataBatchResult
>({
  toolName: 'propose_operational_data_batch',
  render: ({ args, result }) => {
    // A rejected call (`{error: 'invalid_input'}`) renders nothing: the model reissues a corrected
    // call, and falling back to `args` would draw the refused payload or a spinner nothing clears.
    if (isRejectedCardResult(result?.data)) return null;
    // Prefer the server-validated result; fall back to args during the streaming preview window.
    const data = (result?.data ?? args) as ProposeOperationalDataBatchData;
    // Apply-access denial — no card; the assistant's prose tells the user they lack edit access.
    if (result?.data && data.dataAvailable === false) {
      return null;
    }
    // Still streaming: the tool trail shows the step (PLT-7912).
    if (!data.companyId || !Array.isArray(data.datasets) || data.datasets.length === 0) return null;
    return <ProposeOperationalDataBatchContent data={data} />;
  },
});

// ---------------------------------------------------------------------------
// `propose_sustainability_indicators_batch` — chat-only sign-off card (PLT-5088).
//
// The server-side tool (`mcp/tools/propose-sustainability-indicators-batch.ts`)
// schema-validates and echoes the proposal back. This UI renders it as a
// `<SustainabilityIndicatorsBatchCard>`. The card is company-keyed (the apply
// endpoint takes companyId, not a variant), so no variant resolution is needed;
// it owns its own state and POSTs the signed-off set directly to the batch-apply
// endpoint, independent of assistant-ui.
// ---------------------------------------------------------------------------

interface ProposeSustainabilityIndicatorsBatchData {
  companyId: string;
  companyName?: string;
  proposalId: string;
  reportYear?: number;
  summaryMarkdown?: string;
  indicators: SustainabilityIndicatorsBatchCardProps['indicators'];
  // PLT-5088: when the user can't apply (no edit access) the server returns `dataAvailable: false`
  // and no indicators — no card renders; the assistant's prose carries the message.
  dataAvailable?: false;
}

interface ProposeSustainabilityIndicatorsBatchResult {
  data: ProposeSustainabilityIndicatorsBatchData & { _interpretation?: unknown };
}

const ProposeSustainabilityIndicatorsBatchToolUI = makeAssistantToolUI<
  ProposeSustainabilityIndicatorsBatchData,
  ProposeSustainabilityIndicatorsBatchResult
>({
  toolName: 'propose_sustainability_indicators_batch',
  render: ({ args, result }) => {
    // Rejected call — render nothing, as for `propose_operational_data_batch` above.
    if (isRejectedCardResult(result?.data)) return null;
    // Prefer the server-validated result; fall back to args during the streaming preview window.
    const data = (result?.data ?? args) as ProposeSustainabilityIndicatorsBatchData;
    // Apply-access denial — no card; the assistant's prose tells the user they lack edit access.
    if (result?.data && data.dataAvailable === false) {
      return null;
    }
    // Still streaming: the tool trail shows the step (PLT-7912).
    if (!data.companyId || !Array.isArray(data.indicators) || data.indicators.length === 0) return null;
    return (
      <SustainabilityIndicatorsBatchCard
        companyId={data.companyId}
        companyName={data.companyName}
        proposalId={data.proposalId}
        reportYear={data.reportYear}
        summaryMarkdown={data.summaryMarkdown}
        indicators={data.indicators}
      />
    );
  },
});

// ---------------------------------------------------------------------------
// `request_tailoring_selection` — chat-only select-then-evaluate gate (PLT-4968).
//
// The server-side tool (`mcp/tools/request-tailoring-selection.ts`) schema-
// validates and echoes the candidate list back. This UI renders it as a
// `<TailoringSelectionCard>`. Unlike the propose card, this one carries no
// write path: on confirm it appends a user turn (via the card's own
// `useAssistantRuntime`) naming the kept tailorings, which re-enters the agent
// to evaluate only that subset. No company-variant resolution is needed here —
// nothing is committed, so the card never touches the apply endpoint.
// ---------------------------------------------------------------------------

const RequestTailoringSelectionToolUI = makeAssistantToolUI<
  TailoringSelectionCardProps,
  { data: TailoringSelectionCardProps & { _interpretation?: unknown } }
>({
  toolName: 'request_tailoring_selection',
  render: ({ args, result }) => {
    // Rejected or failed call — render nothing, as for `propose_tailoring_batch` above.
    if (isRejectedCardResult(result?.data)) return null;
    // Prefer the server-validated result; fall back to args during the streaming preview window.
    const data = (result?.data ?? args) as TailoringSelectionCardProps;
    // Still streaming: the tool trail shows the step (PLT-7912).
    if (!data.companyId || !Array.isArray(data.items) || data.items.length === 0) return null;
    return (
      <TailoringSelectionCard
        companyId={data.companyId}
        selectionId={data.selectionId}
        priorDmaLabel={data.priorDmaLabel}
        promptMarkdown={data.promptMarkdown}
        items={data.items}
      />
    );
  },
});

// ---------------------------------------------------------------------------
// `request_own_insights_start` — turning own insights on from the chat (PLT-7869).
//
// The server-side tool (`mcp/tools/request-own-insights-start.ts`) checks the user may turn own insights on for
// the company and echoes it back, or answers `dataAvailable: false` with no card. `<OwnInsightsStartCard>` makes
// the change through the company page's route and re-enters the agent once the tailored version is ready.
// ---------------------------------------------------------------------------

const RequestOwnInsightsStartToolUI = makeAssistantToolUI<
  OwnInsightsStartCardProps,
  { data: OwnInsightsStartCardProps & { dataAvailable?: boolean; _interpretation?: unknown } }
>({
  toolName: 'request_own_insights_start',
  render: ({ result }) => {
    if (isRejectedCardResult(result?.data) || result?.data.dataAvailable === false) return null;
    // No streaming preview: the card is drawn from the server's answer, which carries the resolved company id.
    // Until then the tool trail shows the step (PLT-7912).
    if (!result?.data.companyId) return null;
    return <OwnInsightsStartCard companyId={result.data.companyId} companyName={result.data.companyName} />;
  },
});

// ---------------------------------------------------------------------------
// `propose_site_locations` — in-chat site-location verification card (PLT-5467).
//
// The server-side tool (`mcp/tools/propose-site-locations.ts`) schema-validates and echoes the
// proposal back; this UI renders it as a `<SiteLocationsBatchCard>`. The card owns its editable-row
// state and POSTs the reviewed sites straight to the site-locations save / publish endpoints, a
// path independent of assistant-ui, so it is unaffected by message re-renders once mounted.
//
// The proposal carries `companyId` but not `variantId` (variants are not part of the chat surface),
// so the variant is resolved here via `useGetCompanyVariantId`, the same way the tailoring card does
// it; publish then expands across the tailoring link server-side. With no resolvable variant the
// card disables its commit actions and explains why.
// ---------------------------------------------------------------------------

// `source` is optional here although the tool now requires it: a proposal made before it did, still
// held by an open chat, carries none. Its new rows were then always parsed or typed in the chat, which
// was recorded as 'file-upload' before 'chat' existed — never 'manual', which a row without a source
// would save as.
type ProposeSiteLocationsData = Omit<SiteLocationsBatchCardProps, 'variantId' | 'source' | 'firstApproval'> & {
  source?: SiteLocationsBatchCardProps['source'];
  dataAvailable?: false;
  /** PLT-6999: the site review before a custom company's first results; see the card. */
  firstApproval?: true;
};

function ProposeSiteLocationsContent({ data, toolCallId }: { data: ProposeSiteLocationsData; toolCallId: string }) {
  const variantId = useGetCompanyVariantId(data.companyId);
  // A review stands where the thread says, as a product set card does: approved once the publish its
  // approval leads to has followed it, so it is not offered again when the chat is reopened.
  const reviewStanding = useThread(state =>
    data.firstApproval ? siteReviewStanding(state.messages, toolCallId, data.companyId) : 'current',
  );
  // A proposal saved after the review opened leaves that review on the old set (PLT-7368).
  const reviewOpenedBefore = useThread(state =>
    data.firstApproval ? siteReviewOpenedBefore(state.messages, toolCallId, data.companyId) : false,
  );
  // An approved review lists the sites its publish recorded (PLT-7453).
  const publishResult = useThread(state =>
    data.firstApproval ? approvingPublishResult(state.messages, toolCallId, data.companyId) : undefined,
  );
  const approvedSites = reviewStanding === 'approved' ? approvedSitesOf(publishResult) : undefined;
  return (
    <SiteLocationsBatchCard
      companyId={data.companyId}
      companyName={data.companyName}
      variantId={variantId ?? null}
      offerFinancialStatements={data.offerFinancialStatements}
      proposalId={data.proposalId}
      mode={data.mode}
      source={data.source ?? 'file-upload'}
      summaryMarkdown={data.summaryMarkdown}
      sites={data.sites}
      savedSiteIdsAtProposal={data.savedSiteIdsAtProposal}
      firstApproval={data.firstApproval ? { standing: reviewStanding, reviewOpenedBefore, approvedSites } : undefined}
      review={data.review}
      customCompanyRecalculation={data.customCompanyRecalculation}
      coordinateLookup={data.coordinateLookup}
      replayed={isSeededToolCallId(toolCallId)}
    />
  );
}

const ProposeSiteLocationsToolUI = makeAssistantToolUI<
  ProposeSiteLocationsData,
  { data: ProposeSiteLocationsData & { _interpretation?: unknown } }
>({
  toolName: 'propose_site_locations',
  render: ({ result, toolCallId }) => {
    // Rendered from the server result only, never from the streamed args the other proposal cards
    // preview. The result is what carries the company the access check resolved, its name, and the
    // saved set the proposal was made against, and the card fixes all three when it first mounts —
    // a card mounted from args would keep the agent's company name and no baseline once the result
    // arrived. Until then the tool trail shows the step (PLT-7912).
    if (!result) return null;
    const data = result.data as ProposeSiteLocationsData | undefined;
    // No card for a commit-access denial (the assistant's prose tells the user they cannot save to
    // this company) or for a call the server refused (the `{error}` envelope; the next round says
    // why). Nothing rather than a spinner that would never resolve. A build-on-top proposal with no
    // sites is still a card: it is how a request to remove saved sites opens the saved set for the
    // user to delete rows from. An empty replace never gets here, since the server refuses it.
    if (!data || data.dataAvailable === false || !data.companyId || !Array.isArray(data.sites)) {
      return null;
    }
    if (data.sites.length === 0 && data.mode !== 'build-on-top') {
      return null;
    }
    return <ProposeSiteLocationsContent data={data} toolCallId={toolCallId} />;
  },
});

// ---------------------------------------------------------------------------
// `review_supplier_import` — the review card of a supplier list uploaded in chat (PLT-7096).
//
// The tool result is a summary for the agent; the card reads the whole review, row by row, from the
// card-state routes and saves the user's changes there, so a reopened conversation shows the import
// as it stands now rather than as it was when the tool ran.

type ReviewSupplierImportResult = { importId?: string; dataAvailable?: false; error?: unknown };

const ReviewSupplierImportToolUI = makeAssistantToolUI<{ importId?: string }, { data?: ReviewSupplierImportResult }>({
  toolName: 'review_supplier_import',
  render: ({ args, result }) => {
    // Until the result, the tool trail shows the step (PLT-7912).
    if (!result) return null;
    const data = result.data;
    // Nothing for a list that has not been sorted or a refused call: the agent's prose says why.
    if (!data || data.dataAvailable === false || data.error !== undefined) return null;
    const importId = typeof args.importId === 'string' ? args.importId : data.importId;
    if (!importId) return null;
    return <SupplierImportReviewCard importId={importId} />;
  },
});

// ---------------------------------------------------------------------------
// `request_upright_review` — the "Ask Upright to review this" card (PLT-7846).
//
// The tool result echoes the fields the agent filled in; the card sends them, or records a cancel,
// itself, and reads that answer back on mount so a reopened conversation shows it closed.

type RequestUprightReviewResult = Partial<ReviewRequestFields> & { error?: unknown };

/**
 * The card's coordinates: the thread id and message index the adapter stamps on every message, and
 * the history loader stamps the same on a reopened one. The tool call id is not stable across a
 * reopen, so it cannot identify the card.
 */
function RequestUprightReviewContent({ draft }: { draft: ReviewRequestFields }) {
  const custom = useMessage(m => m.metadata.custom as { threadId?: unknown; messageIndex?: unknown } | undefined);
  const threadId = custom?.threadId;
  const messageIndex = custom?.messageIndex;
  if (typeof threadId !== 'string' || typeof messageIndex !== 'number') return null;
  return <ReviewRequestCard conversationId={threadId} messageIndex={messageIndex} draft={draft} />;
}

const RequestUprightReviewToolUI = makeAssistantToolUI<
  Partial<ReviewRequestFields>,
  { data?: RequestUprightReviewResult }
>({
  toolName: 'request_upright_review',
  render: ({ result }) => {
    // Nothing until the result: the card's fields are the validated ones, not the streaming args.
    if (!result) return null;
    const data = result.data;
    if (!data || isRejectedCardResult(data)) return null;
    if (!data.question || !data.problem || !data.expected) return null;
    return (
      <RequestUprightReviewContent
        draft={{ question: data.question, problem: data.problem, expected: data.expected, source: data.source ?? '' }}
      />
    );
  },
});

// ---------------------------------------------------------------------------
// `propose_tailoring_removal` — chat-only undo card (PLT-6322).
//
// The server-side tool (`mcp/tools/propose-tailoring-removal.ts`) schema-
// validates and echoes the removal list back. This UI renders it as a
// `<TailoringRemovalCard>`, which POSTs the selected tailoringIds to the
// batch-remove endpoint itself.
//
// `variantId` is resolved the same way the apply card resolves it — from the
// company the agent named, not from the chat's currently-selected variant,
// because a chat session can outlive a company-page mount.
// ---------------------------------------------------------------------------

interface ProposeTailoringRemovalData {
  companyId: string;
  removalId: string;
  promptMarkdown?: string;
  items: TailoringRemovalCardProps['items'];
  // PLT-7899: on the server result only.
  ownInsightRows?: TailoringRemovalCardProps['ownInsightRows'];
  // When the user can't remove (no edit access / tailoring not enabled) the server returns
  // `dataAvailable: false` and no items — no card renders; the assistant's prose carries the message.
  dataAvailable?: false;
}

function ProposeTailoringRemovalContent({ data }: { data: ProposeTailoringRemovalData }) {
  const variantId = useGetCompanyVariantId(data.companyId);
  return (
    <TailoringRemovalCard
      companyId={data.companyId}
      variantId={variantId ?? null}
      removalId={data.removalId}
      promptMarkdown={data.promptMarkdown}
      items={data.items}
      ownInsightRows={data.ownInsightRows}
    />
  );
}

const ProposeTailoringRemovalToolUI = makeAssistantToolUI<
  ProposeTailoringRemovalData,
  { data: ProposeTailoringRemovalData & { _interpretation?: unknown } }
>({
  toolName: 'propose_tailoring_removal',
  render: ({ args, result }) => {
    // Rejected or failed call — render nothing, as for `propose_tailoring_batch` above.
    if (isRejectedCardResult(result?.data)) return null;
    // Prefer the server-validated result; fall back to args during the streaming preview window.
    const data = (result?.data ?? args) as ProposeTailoringRemovalData;
    // Access denial — no card; the assistant's prose tells the user why.
    if (result?.data && data.dataAvailable === false) {
      return null;
    }
    // Still streaming: the tool trail shows the step (PLT-7912).
    if (!data.companyId || !Array.isArray(data.items) || data.items.length === 0) return null;
    return <ProposeTailoringRemovalContent data={data} />;
  },
});

// ---------------------------------------------------------------------------
// Registry — consumed by the main `Thread` component's `tools` prop.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// PLT-5031 — physical climate risk (+ its two sub-tools).
//
// These tools spend minutes resolving company sites (external geolocation) and
// looking up per-site climate risk. The tool trail shows them running, and the
// coarse `status` progress the aggregate tool streams (see the chat handler)
// advances its line ("Found 7 sites, assessing…", "Assessed 3 of 7…") (PLT-7912).
// ---------------------------------------------------------------------------

// PLT-5163: renders the `climate-heatmap` MCP app card when the backend emits
// `_ui.appName` (set via the tool's `mcpAppResourceUri`); `McpToolUI` falls
// back to the collapsible JSON view for no-data / error responses without it.
// PLT-5163 (Roni): sentence case ("Physical climate risk", not Title Case) so the
// collapsed card header matches the card body's own subtitle. Other tool cards keep
// Title Case; this one is intentionally cased to match the climate card's heading.
const PhysicalClimateRiskToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'get_company_physical_climate_risk',
  render: ({ result, toolCallId }) => (
    <McpToolUI baseLabel="Physical climate risk" result={result} toolCallId={toolCallId} />
  ),
});

const IdentifyCompanySitesToolUI = makeAssistantToolUI<{ companyName?: string }, ToolResultWithUi>({
  toolName: 'identify_company_sites',
  render: ({ result }) => {
    const limitKind = extractToolLimitError(result);
    return limitKind ? <ChatToolLimitCard kind={limitKind} feature="company site lookups" /> : null;
  },
});

/**
 * PLT-5166: bbox climate-risk raster — renders the `climate-risk-map` MCP
 * app card (multi-panel choropleth). Routed through `McpToolUI` (NOT a
 * status line) because the tool result carries `_ui.appName`; on no-data
 * (`bbox-too-large`, `no-data-for-bbox`, …) the backend omits `_ui` and
 * McpToolUI degrades to the standard fallback.
 */
const ClimateRiskGridToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'get_climate_risk_grid',
  render: ({ result, args, toolCallId }) => {
    const hazard = typeof args.hazard === 'string' ? args.hazard.replace(/_/g, ' ') : null;
    return (
      <McpToolUI
        baseLabel="Climate Risk Map"
        contextSuffix={hazard}
        result={result}
        toolCallId={toolCallId}
        limitFeature="climate risk map assessments"
      />
    );
  },
});

/**
 * PLT-7111: climate risk over time — renders the `climate-trajectory` MCP app card
 * (trajectory chart and level timeline). On no-data the backend omits `_ui` and
 * McpToolUI degrades to the standard fallback. The suffix names the locations, so
 * two of these cards in one thread can be told apart while collapsed.
 */
const ClimateRiskTrajectoryToolUI = makeAssistantToolUI<
  { locations?: Array<{ label?: string; lat?: number; lon?: number }> },
  ToolResultWithUi
>({
  toolName: 'get_climate_risk_trajectory',
  render: ({ result, args, toolCallId }) => {
    return (
      <McpToolUI
        baseLabel="Climate risk over time"
        contextSuffix={trajectoryLocationsSuffix(args)}
        result={result}
        toolCallId={toolCallId}
        limitFeature="location climate risk assessments"
      />
    );
  },
});

const LocationClimateRiskToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'get_location_climate_risk',
  render: ({ result }) => {
    const limitKind = extractToolLimitError(result);
    return limitKind ? <ChatToolLimitCard kind={limitKind} feature="location climate risk assessments" /> : null;
  },
});

// PLT-5129 / PLT-5214: `compute_financial_effects` has no MCP-app card; the tool trail shows it
// running and done, and holds its "Calculating" step for a few seconds, since a computation that
// returns at once reads as made up (`minRunningMs`, PLT-7912). PLT-5273: a limit hit is a non-null
// error result, so it shows the limit card rather than a done step alone.
const ComputeFinancialEffectsToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'compute_financial_effects',
  render: ({ result }) => {
    const limitKind = extractToolLimitError(result);
    return limitKind ? <ChatToolLimitCard kind={limitKind} feature="financial effects calculations" /> : null;
  },
});

// PLT-5188: `validate_financial_effects_inputs` is the confirmation step before
// `compute_financial_effects`. It has no card: its row in the tool trail is all it shows.
const ValidateFinancialEffectsInputsToolUI = makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
  toolName: 'validate_financial_effects_inputs',
  render: () => null,
});

// PLT-6906: every custom impact framework tool that returns the framework view mounts the same
// card, so the mapping on screen is always the latest save. Expanded by default like the other
// editing cards (DD company, fund review): the card is the thing being edited, not a detail to
// open. The themes proposal that comes before the framework exists renders on the same card. Each
// tool name stays written as `toolName: '…'`: the app-binding spec in
// `instruction-consistency.spec.ts` looks for that literally.
const customFrameworkToolUI = ({ toolName }: { toolName: string }) =>
  makeAssistantToolUI<Record<string, unknown>, ToolResultWithUi>({
    toolName,
    render: props => (
      <McpToolUI
        baseLabel="Custom framework"
        result={props.result}
        toolCallId={props.toolCallId}
        defaultExpanded={true}
      />
    ),
  });
const ProposeCustomFrameworkThemesToolUI = customFrameworkToolUI({ toolName: 'propose_custom_framework_themes' });
const CreateCustomImpactFrameworkToolUI = customFrameworkToolUI({ toolName: 'create_custom_impact_framework' });
const EditCustomImpactFrameworkToolUI = customFrameworkToolUI({ toolName: 'edit_custom_impact_framework' });
const MarkCustomImpactFrameworkReadyToolUI = customFrameworkToolUI({ toolName: 'mark_custom_impact_framework_ready' });
const RestoreCustomImpactFrameworkVersionToolUI = customFrameworkToolUI({
  toolName: 'restore_custom_impact_framework_version',
});
const GetCustomImpactFrameworkToolUI = customFrameworkToolUI({ toolName: 'get_custom_impact_framework' });

// PLT-7912: each tool UI that is a trail step also draws the turn's tool trail, when its call is
// the turn's first step (`withToolTrail`). The reasoning step has no UI of its own: the trail
// fallback (`ToolTrailFallback`) draws its row.
export const CHAT_TOOL_UIS = withToolTrail([
  // PLT-6906 — custom impact framework card.
  ProposeCustomFrameworkThemesToolUI,
  CreateCustomImpactFrameworkToolUI,
  EditCustomImpactFrameworkToolUI,
  MarkCustomImpactFrameworkReadyToolUI,
  RestoreCustomImpactFrameworkVersionToolUI,
  GetCustomImpactFrameworkToolUI,
  ChatErrorToolUI,
  ChatNoticeToolUI,
  ChatTokenLimitToolUI,
  NetImpactToolUI,
  SearchToolUI,
  ContributingProductsToolUI,
  DmaResultToolUI,
  DmaMatrixToolUI,
  SupplierRiskToolUI,
  SupplierBaseToolUI,
  InvestorReportToolUI,
  SingleMatterToolUI,
  SupportingArticlesToolUI,
  FinancialEffectsToolUI,
  ProductImpactReasoningToolUI,
  ProductValueChainToolUI,
  SdgAlignmentToolUI,
  EuTaxonomyToolUI,
  SfdrPaisToolUI,
  ScreenCompaniesToolUI,
  SearchKnowledgeBaseToolUI,
  MultiCompanyNetImpactToolUI,
  MultiCompanyDmaToolUI,
  GroupExposureToolUI,
  // PLT-4620 / PLT-4621 — DD-company chat tools (CompanyCard / ProductSetCard).
  CreateDdCompanyToolUI,
  // PLT-6267 — supplier create, same CompanyCard.
  CreateSupplierToolUI,
  GetDdCompanyStatusToolUI,
  GetDdProductSetToolUI,
  PublishDdProductSetToolUI,
  UpdateDdProductSetToolUI,
  SaveDdProductSetFromFilesToolUI,
  // PLT-5523 — fund review + confirm card (FundReviewCard).
  ReviewStagedFundToolUI,
  // PLT-5583 — before/after review card for a new fund time-version.
  ReviewFundVersionToolUI,
  // PLT-4891 — agentic financial-statement editable review card.
  ExtractFinancialStatementsToolUI,
  FinancialStatementOfferToolUI,
  // Refreshes the financial statements page after a save from the draft card.
  PublishDraftToolUI,
  CompareReleasesToolUI,
  // PLT-5031 — physical climate risk family.
  PhysicalClimateRiskToolUI,
  IdentifyCompanySitesToolUI,
  LocationClimateRiskToolUI,
  // PLT-5129 — user-supplied financial-effects computation.
  ComputeFinancialEffectsToolUI,
  // PLT-5188 — validate-inputs confirmation step before compute.
  ValidateFinancialEffectsInputsToolUI,
  ClimateRiskGridToolUI,
  // PLT-7111 — climate risk over time, per location.
  ClimateRiskTrajectoryToolUI,
  StyleGuideToolUI,
  PresentHeadlineKpisToolUI,
  EmitCalloutToolUI,
  RequestTailoringSelectionToolUI,
  RequestOwnInsightsStartToolUI,
  ProposeTailoringBatchToolUI,
  ProposeSiteLocationsToolUI,
  // PLT-7096 — supplier import review card.
  ReviewSupplierImportToolUI,
  // PLT-7846 — "Ask Upright to review this" card.
  RequestUprightReviewToolUI,
  ProposeTailoringRemovalToolUI,
  ProposeOperationalDataBatchToolUI,
  ProposeSustainabilityIndicatorsBatchToolUI,
  WebSearchToolUI,
  CodeExecutionToolUI,
  ExecutePythonToolUI,
  SuggestFollowupsToolUI,
]);
